import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ASSETS } from './assetManifest';
import { CONSTANTS } from './data';
import { Howl } from 'howler';
import { AnimatePresence, motion } from 'framer-motion';
import html2canvas from 'html2canvas';

type ComboHistoryItem = {
  a: string;
  b: string;
  result: string;
  chapter: number;
  text: string;
};

type DoorLocation = 'left' | 'center' | 'right';

type GameState = 'mainMenu' | 'intro' | 'playing' | 'paused' | 'realityCollapse' | 'gameEnd' | 'tutorial';

type IntroPhase = 'opening' | 'candle' | 'narrative1' | 'narrative2' | 'narrative3' | 'transition';

type VolumeSettings = {
  master: number;
  sfx: number;
  music: number;
  muted: boolean;
  brightness: number;
};

type PlacedElement = {
  id: number;
  name:string;
  x: number;
  y: number;
  isAboutToMove?: boolean;
  anchoredUntil?: number;
}

type TemporaryEffect = {
  id: string;
  key: number;
  duration: number;
};

type NarrativeEffect = {
  id: string;
  key: number;
  type: 'filter' | 'overlay' | 'transform';
  duration: number;
  pulses?: number;
};

type CombineResult = 
  | { type: 'fail' }
  | { type: 'element'; name: string; isNew: boolean }
  | { type: 'background'; isNew: boolean };

const COMBINATION_DISTANCE = 60;
const PLACED_ELEMENT_SIZE = 60;

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkIsMobile = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        setIsMobile(checkIsMobile());
    }, []);
    return isMobile;
};

const Tooltip = ({ content, x, y }: { content: string; x: number; y: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 5 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 5 }}
    transition={{ duration: 0.15 }}
    className="fixed z-50 px-2 py-1 text-sm font-serif text-gray-300 bg-black/80 border border-white/10 rounded-md pointer-events-none shadow-lg"
    style={{ left: x, top: y }}
  >
    {content}
  </motion.div>
);

function normalizeKey(a: string, b: string) {
  const [x, y] = [a, b].sort();
  return `${x}|${y}`;
}

function getRandomInt(min: number, max: number) {
  const mi = Math.ceil(min);
  const ma = Math.floor(max);
  return Math.floor(Math.random() * (ma - mi + 1)) + mi;
}

function randomChoice<T>(arr: T[]): T {
  if (!arr || arr.length === 0) {
    throw new Error('randomChoice called with empty array');
  }
  return arr[Math.floor(Math.random() * arr.length)];
}

function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

function usePersistentState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    const raw = localStorage.getItem(key);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        // MIGRATION: if key is narrative and it's a string, wrap it in an array
        if (key === 'udch_narrative' && typeof parsed === 'string') {
          return [parsed] as unknown as T;
        }
        return parsed as T;
      } catch (e) {
        console.error('Failed to parse from localStorage', e);
        return initial;
      }
    }
    return initial;
  });
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue] as const;
}

// Deterministic RNG for AI generation (LCG)
function makeRng(seed: number) {
  let s = seed >>> 0;
  return {
    next: () => {
      s = (s * 1664525 + 1013904223) >>> 0;
      return s / 0xffffffff;
    },
    pick<T>(arr: T[]): T {
      if (!arr || arr.length === 0) {
        throw new Error('RNG pick called with empty array');
      }
      s = (s * 1664525 + 1013904223) >>> 0;
      return arr[Math.floor((s / 0xffffffff) * arr.length)];
    },
  };
}

function titleCaseName(input: string) {
  const lowerWords = new Set(['of', 'the', 'and', 'in', 'to', 'for', 'on', 'with', 'at', 'from', 'by']);
  return input
    .split(' ')
    .map((w, i) => {
      const lw = w.toLowerCase();
      if (i > 0 && lowerWords.has(lw)) return lw;
      return lw.charAt(0).toUpperCase() + lw.slice(1);
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// AI Narrator to refine text for better flow and horror tone
function refineNarrativeText(text: string, seed: number, drift: number): string {
  const rng = makeRng(seed);
  const config = CONSTANTS.aiNarrator;

  // Helper to combine a buffer of sentences into one complex sentence
  const combineSentences = (sentences: string[]): string => {
    if (sentences.length === 0) return '';
    if (sentences.length === 1) return sentences[0];

    let result = sentences[0].trim().replace(/[.!?]$/, '');
    for (let i = 1; i < sentences.length; i++) {
      const connector = rng.pick(config.connectors.standard);
      let next = sentences[i].trim();
      next = next.charAt(0).toLowerCase() + next.slice(1).replace(/[.!?]$/, '');
      result += ` ${connector} ${next}`;
    }
    return result + '.';
  };

  // 1. Sentence Segmentation
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];

  // 2. Refine each sentence individually
  const refinedSentences = sentences.map((sentence) => {
    let s = sentence.trim();

    // 2a. Starter Replacement
    for (const starter of config.sentenceStarters.toReplace) {
      const regex = new RegExp(`^${starter}\\s*,?\\s*`, 'i');
      if (regex.test(s)) {
        s = s.replace(regex, '');
        s = s.charAt(0).toUpperCase() + s.slice(1);
        if (rng.next() < config.sentenceStarters.prefixChance) {
          const prefix = rng.pick(config.sentenceStarters.prefixes);
          s = `${prefix} ${s.toLowerCase()}`;
        }
        break;
      }
    }

    // 2b. Vocabulary Enhancement (based on drift)
    const horrorVocab = config.horrorVocabulary;
    const driftTier = drift < 33 ? 'low' : drift < 66 ? 'medium' : 'high';
    let words = s.split(' ');
    words = words.map(word => {
      const cleanWord = word.replace(/[.,!?]/g, '').toLowerCase();
      if (horrorVocab.replacements[cleanWord] && rng.next() < horrorVocab.chance) {
        const replacementPool = horrorVocab.replacements[cleanWord][driftTier] || horrorVocab.replacements[cleanWord]['low'];
        return rng.pick(replacementPool) + word.substring(cleanWord.length);
      }
      return word;
    });
    s = words.join(' ');

    // 2c. Phrase Replacement
    for (const [phrase, replacements] of Object.entries(config.phraseReplacements)) {
      const regex = new RegExp(`\\b${phrase}\\b`, 'gi');
      if (regex.test(s)) {
        s = s.replace(regex, rng.pick(replacements));
      }
    }

    return s;
  });

  // 3. Reconstruct into sentences of varying complexity
  let finalSentences: string[] = [];
  let sentenceBuffer: string[] = [];
  for (const sentence of refinedSentences) {
    sentenceBuffer.push(sentence);
    if (rng.next() < config.sentenceComplexity.breakChance || sentenceBuffer.length >= config.sentenceComplexity.maxClauses) {
      finalSentences.push(combineSentences(sentenceBuffer));
      sentenceBuffer = [];
    }
  }
  if (sentenceBuffer.length > 0) {
    finalSentences.push(combineSentences(sentenceBuffer));
  }

  return finalSentences.join(' ');
}


// New: generate a deterministic AI image URL for an element name (Project B idea)
function getAiImageUrl(name: string) {
  return `https://picsum.photos/seed/${encodeURIComponent(name)}/64/64`;
}

function applyNarrativeTone(text: string, drift: number): string {
  const toneConfig = CONSTANTS.narrativeTones;
  const band = [...toneConfig.bands].reverse().find(b => drift >= b.threshold);

  if (!band || (!band.prefixChance && !band.suffixChance && !band.fragmentChance)) {
    return text;
  }

  let modifiedText = text;

  // Apply fragmentation
  if (band.fragmentChance && Math.random() < band.fragmentChance) {
    const words = modifiedText.split(' ');
    if (words.length > 5) {
      const start = Math.floor(Math.random() * (words.length - 4));
      const end = start + getRandomInt(2, 5);
      return words.slice(start, end).join(' ') + '...';
    }
  }

  // Apply prefix
  if (band.prefixes && band.prefixChance && Math.random() < band.prefixChance) {
    modifiedText = `${randomChoice(band.prefixes)} ${modifiedText.toLowerCase()}`;
  }

  // Apply suffix
  if (band.suffixes && band.suffixChance && Math.random() < band.suffixChance) {
    modifiedText = `${modifiedText} ${randomChoice(band.suffixes)}`;
  }

  return modifiedText;
}

// Animated Logo Component
const AnimatedLogo = ({ width = 200, lockVariant, onVariantChange }: { width?: number; lockVariant?: number, onVariantChange?: (variant: number) => void }) => {
  const [currentVariant, setCurrentVariant] = useState(lockVariant ?? 0);
  const [isGlitching, setIsGlitching] = useState(false);
  const changeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const glitchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const variants = CONSTANTS.animatedLogo.variants;
  const config = CONSTANTS.animatedLogo;
  const aspectRatio = 600 / 1600;
  const height = width * aspectRatio;

  const scheduleNextChange = useCallback(() => {
    if (lockVariant !== undefined) return; // Don't change if locked
    
    const delay = getRandomInt(config.minInterval, config.maxInterval);
    changeTimeoutRef.current = setTimeout(() => {
      setCurrentVariant((prev) => {
        let next = Math.floor(Math.random() * variants.length);
        while (next === prev && variants.length > 1) {
          next = Math.floor(Math.random() * variants.length);
        }
        if (onVariantChange) onVariantChange(next);
        return next;
      });
      scheduleNextChange();
    }, delay);
  }, [variants.length, config, lockVariant, onVariantChange]);

  const scheduleNextGlitch = useCallback(() => {
    if (!config.glitchMinInterval || !config.glitchMaxInterval) return;
    const delay = getRandomInt(config.glitchMinInterval, config.glitchMaxInterval);
    glitchTimeoutRef.current = setTimeout(() => {
      setIsGlitching(true);
      setTimeout(() => setIsGlitching(false), config.glitchDuration);
      scheduleNextGlitch();
    }, delay);
  }, [config]);

  useEffect(() => {
    if (lockVariant !== undefined) {
      setCurrentVariant(lockVariant);
      return;
    }
    
    scheduleNextChange();
    scheduleNextGlitch();
    return () => {
      if (changeTimeoutRef.current) clearTimeout(changeTimeoutRef.current);
      if (glitchTimeoutRef.current) clearTimeout(glitchTimeoutRef.current);
    };
  }, [scheduleNextChange, scheduleNextGlitch, lockVariant]);

  const getLogoAsset = (variantIndex: number) => {
    const variantKey = variants[variantIndex];
    return (ASSETS.logos as any)[variantKey];
  };

  return (
    <motion.div
      className="relative"
      style={{ width: width, height: height }}
      animate={isGlitching ? {
        filter: [
          'brightness(1) contrast(1) blur(0px)',
          'brightness(1.8) contrast(1.5) blur(0.5px) hue-rotate(15deg)',
          'brightness(0.9) contrast(1.1) blur(0px)',
          'brightness(1) contrast(1) blur(0px)',
        ],
        scale: [1, 1.02, 0.98, 1],
      } : {}}
      transition={isGlitching ? { duration: config.glitchDuration / 1000, ease: 'steps(3, end)' } : {}}
    >
      <AnimatePresence>
        <motion.img
          key={currentVariant}
          src={getLogoAsset(currentVariant)?.path}
          alt="Logo"
          className="absolute inset-0 w-full h-full object-contain opacity-90"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.9 }}
          exit={{ opacity: 0 }}
          transition={{ duration: config.transitionDuration / 1000, ease: 'easeInOut' }}
        />
      </AnimatePresence>
    </motion.div>
  );
};

const MainMenuBackground = ({
  getSfx,
  playWhisper,
  volumeSettings,
  isTransitioning,
  isMobile,
}: {
  getSfx: (name: keyof typeof ASSETS.sfx) => Howl;
  playWhisper: () => void;
  volumeSettings: VolumeSettings;
  isTransitioning: boolean;
  isMobile: boolean;
}) => {
  const [doorVisible, setDoorVisible] = useState(false);
  const [doorKey, setDoorKey] = useState(0);
  const menuAmbientRef = useRef<Howl | null>(null);
  const footstepTimerRef = useRef<NodeJS.Timeout | null>(null);
  const doorTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sound setup and timers
  useEffect(() => {
    // Create and play ambient sound
    const ambient = new Howl({
      src: [ASSETS.sfx.menuAmbient.path],
      loop: true,
      volume: volumeSettings.muted ? 0 : volumeSettings.master * volumeSettings.music,
    });
    ambient.play();
    menuAmbientRef.current = ambient;

    // Get other SFX, which registers them for volume control
    const footsteps = getSfx('menuFootsteps');
    const knock = getSfx('menuKnock');

    footstepTimerRef.current = setInterval(() => {
      footsteps.play();
    }, getRandomInt(CONSTANTS.mainMenuAnimation.footstepIntervalMin, CONSTANTS.mainMenuAnimation.footstepIntervalMax));

    const scheduleDoor = () => {
      doorTimerRef.current = setTimeout(() => {
        setDoorKey(k => k + 1);
        setDoorVisible(true);

        if (Math.random() < 0.4) {
          knock.play();
        } else {
          playWhisper();
        }

        setTimeout(() => {
          setDoorVisible(false);
          if (doorTimerRef.current) {
            scheduleDoor();
          }
        }, CONSTANTS.mainMenuAnimation.doorVisibleDuration);

      }, getRandomInt(CONSTANTS.mainMenuAnimation.doorAppearIntervalMin, CONSTANTS.mainMenuAnimation.doorAppearIntervalMax));
    };
    scheduleDoor();

    // Cleanup on unmount
    return () => {
      menuAmbientRef.current?.stop();
      menuAmbientRef.current?.unload();
      if (footstepTimerRef.current) clearInterval(footstepTimerRef.current);
      if (doorTimerRef.current) clearTimeout(doorTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSfx, playWhisper]);

  // Handle transition start
  useEffect(() => {
    if (isTransitioning) {
      menuAmbientRef.current?.fade(menuAmbientRef.current.volume(), 0, 500);
      if (footstepTimerRef.current) {
        clearInterval(footstepTimerRef.current);
        footstepTimerRef.current = null;
      }
      if (doorTimerRef.current) {
        clearTimeout(doorTimerRef.current);
        doorTimerRef.current = null;
      }
      setDoorVisible(false);
    }
  }, [isTransitioning]);

  // Update ambient volume when settings change
  useEffect(() => {
    if (menuAmbientRef.current) {
      const masterVol = volumeSettings.muted ? 0 : volumeSettings.master;
      menuAmbientRef.current.volume(masterVol * volumeSettings.music);
    }
  }, [volumeSettings]);

  // Dust Particles
  const particles = useMemo(() => Array.from({ length: isMobile ? 20 : 50 }).map((_, i) => ({
    id: i,
    duration: getRandomInt(15, 40),
    delay: getRandomInt(0, 40),
    size: getRandomInt(1, 3),
    startY: Math.random() * 100,
    yDrift: getRandomInt(-10, 10),
  })), [isMobile]);

  return (
    <div className="absolute inset-0 w-full h-full bg-black overflow-hidden -z-10">
      {/* Base hallway image with subtle pan/zoom */}
      <motion.img
        src={ASSETS.backgrounds.menuHallway.path}
        className="absolute inset-0 w-full h-full object-cover"
        alt=""
        initial={{ scale: 1, x: 0, y: 0 }}
        animate={{ scale: 1.03, x: -15, y: -5 }}
        transition={{
          duration: 25,
          repeat: Infinity,
          repeatType: 'mirror',
          ease: 'easeInOut',
        }}
      />

      {/* Disappearing Door */}
      <AnimatePresence>
        {doorVisible && (
          <motion.div
            key={doorKey}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: { duration: 4, ease: 'linear' },
            }}
            exit={{
              opacity: 0,
              transition: { duration: 0.3, ease: 'easeOut' },
            }}
          >
            <motion.img
              src={ASSETS.ui.menuDoor.path}
              alt="A mysterious door"
              className="w-auto h-[45vh] max-h-[350px] opacity-80"
              animate={{
                x: [0, 1, -1, -1, 1, 0],
                y: [0, -1, 0, 1, -1, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatType: 'mirror',
                ease: 'easeInOut',
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Center Light Overlay - makes the center area lighter for logo visibility */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at center, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 30%, transparent 60%)',
        }}
        initial={{ opacity: 0.8 }}
        animate={{ opacity: [0.8, 1, 0.9, 1, 0.8] }}
        transition={{
          duration: 12,
          repeat: Infinity,
          repeatType: 'mirror',
          ease: 'easeInOut',
        }}
      />

      {/* Shadow/Vignette Overlay */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, black 90%)',
        }}
        initial={{ opacity: 0.7 }}
        animate={{ opacity: [0.7, 0.85, 0.75, 0.9, 0.7] }}
        transition={{
          duration: 18,
          repeat: Infinity,
          repeatType: 'mirror',
          ease: 'easeInOut',
        }}
      />

      {/* Light Flicker */}
      <motion.div
        className="absolute top-1/4 left-1/3 w-96 h-96"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(200, 200, 180, 0.15) 0%, transparent 70%)',
        }}
        animate={{
          opacity: [0, 1, 0, 0, 1, 0, 1, 1, 0],
          scale: [1, 1.1, 1, 1, 1.05, 1, 1.1, 1, 1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          times: [0, 0.05, 0.1, 0.4, 0.45, 0.5, 0.7, 0.72, 1],
        }}
      />

      {/* Dust Particles */}
      {particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute bg-white/40 rounded-full"
          style={{
            width: p.size,
            height: p.size,
            top: `${p.startY}%`,
            left: '-5%',
          }}
          animate={{
            x: '110vw',
            y: `${p.yDrift}vh`,
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: 'linear',
            repeatType: 'loop',
          }}
        />
      ))}
    </div>
  );
};

// Intro Sequence Component
const IntroSequence = ({
  getSfx,
  playWhisper,
  volumeSettings,
  onComplete,
  isMobile,
}: {
  getSfx: (name: keyof typeof ASSETS.sfx) => Howl;
  playWhisper: () => void;
  volumeSettings: VolumeSettings;
  onComplete: () => void;
  isMobile: boolean;
}) => {
  const [step, setStep] = useState(0);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [showText, setShowText] = useState(false);
  const [interactionState, setInteractionState] = useState<any>(null);
  const [showBreath, setShowBreath] = useState(false);
  const [showAsset, setShowAsset] = useState<any>(null);
  const [showSkip, setShowSkip] = useState(false);

  const sequence = CONSTANTS.intro.sequence;
  const currentScene = sequence[step];
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const introAmbientRef = useRef<Howl | null>(null);
  const heartbeatRef = useRef<Howl | null>(null);

  const advanceStep = useCallback(() => {
    setShowText(false);
    setShowAsset(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (step + 1 >= sequence.length) {
        onComplete();
      } else {
        setStep(s => s + 1);
      }
    }, 1500); // Delay between steps
  }, [step, sequence.length, onComplete]);

  // Setup intro ambient sound
  useEffect(() => {
    const ambient = new Howl({
      src: [ASSETS.sfx.introAmbient.path],
      loop: true,
      volume: volumeSettings.muted ? 0 : volumeSettings.master * volumeSettings.music * 0.6,
    });
    ambient.play();
    introAmbientRef.current = ambient;

    // Show skip button after a delay
    const skipTimer = setTimeout(() => setShowSkip(true), CONSTANTS.intro.timing.skipButtonDelay);

    return () => {
      introAmbientRef.current?.stop();
      introAmbientRef.current?.unload();
      clearTimeout(skipTimer);
    };
  }, [volumeSettings, getSfx]);

  // Update ambient volume when settings change
  useEffect(() => {
    if (introAmbientRef.current) {
      const masterVol = volumeSettings.muted ? 0 : volumeSettings.master;
      introAmbientRef.current.volume(masterVol * volumeSettings.music * 0.6);
    }
  }, [volumeSettings]);

  useEffect(() => {
    if (!currentScene) return;

    // Set background from static assets
    const backgroundAsset = (ASSETS.backgrounds as any)[currentScene.backgroundImage];
    if (backgroundAsset) {
      setBackgroundUrl(backgroundAsset.path);
    }
    setShowText(true);

    // Handle effects
    currentScene.effects?.forEach(effect => {
      switch (effect) {
        case 'whisper': playWhisper(); break;
        case 'doorCreak': getSfx('doorCreak').play(); break;
        case 'flicker': getSfx('flicker').play(); break;
        case 'heartbeat':
          const heartbeat = getSfx('heartbeat');
          heartbeat.play();
          heartbeatRef.current = heartbeat;
          break;
      }
    });

    // Handle cues
    if (currentScene.cues?.cold === 'breath') {
      setShowBreath(true);
      setTimeout(() => setShowBreath(false), 4000);
    }

    // Handle assets
    if (currentScene.asset) {
      setShowAsset(currentScene.asset);
    }

    // Handle interaction
    if (currentScene.interaction) {
      setInteractionState(currentScene.interaction);
    } else {
      setInteractionState(null);
      if (currentScene.duration > 0) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(advanceStep, currentScene.duration);
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [step, currentScene, getSfx, playWhisper, advanceStep]);

  useEffect(() => {
    const heartbeat = heartbeatRef.current;
    return () => {
      heartbeat?.stop();
    };
  }, []);

  useEffect(() => {
    if (heartbeatRef.current && heartbeatRef.current.playing()) {
      const masterVol = volumeSettings.muted ? 0 : volumeSettings.master;
      heartbeatRef.current.volume(masterVol * volumeSettings.sfx * 0.6);
    }
  }, [volumeSettings, getSfx]);

  const handleInteraction = () => {
    if (interactionState?.type === 'candle') {
      getSfx('candleLight').play();
      setInteractionState({ ...interactionState, lit: true });
      setTimeout(advanceStep, 2000);
    }
  };

  const handleSkip = () => {
    heartbeatRef.current?.stop();
    onComplete();
  };

  // Dust particles for atmosphere
  const dustParticles = useMemo(() => Array.from({ length: isMobile ? 15 : 30 }).map((_, i) => ({
    id: i,
    duration: getRandomInt(20, 50),
    delay: getRandomInt(0, 30),
    size: getRandomInt(1, 2),
    startY: Math.random() * 100,
    yDrift: getRandomInt(-5, 5),
  })), [isMobile]);

  return (
    <div
      className="fixed inset-0 bg-black text-white overflow-hidden flex items-center justify-center"
      style={{ filter: `brightness(${volumeSettings.brightness})` }}
    >
      <AnimatePresence>
        {backgroundUrl && (
          <motion.img
            key={backgroundUrl}
            src={backgroundUrl}
            alt="Horror background"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 0.5, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3, ease: 'linear' }}
          />
        )}
      </AnimatePresence>

      <motion.div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(ellipse at center, transparent 50%, black 100%)' }}
        initial={{ opacity: 0.6 }}
        animate={{ opacity: interactionState?.lit ? 0.4 : 0.8 }}
        transition={{ duration: 2 }}
      />

      {/* Dust particles */}
      {dustParticles.map(p => (
        <motion.div
          key={p.id}
          className="absolute bg-white/20 rounded-full"
          style={{
            width: p.size,
            height: p.size,
            top: `${p.startY}%`,
            left: '-5%',
          }}
          animate={{
            x: '110vw',
            y: `${p.yDrift}vh`,
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: 'linear',
            repeatType: 'loop',
          }}
        />
      ))}

      <div className="relative z-10 w-full max-w-4xl px-8">
        <AnimatePresence>
          {showText && (
            <motion.p
              key={step}
              className="text-center text-xl sm:text-2xl font-serif text-gray-300 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5 }}
            >
              {currentScene.text}
            </motion.p>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {interactionState?.type === 'candle' && (
            <motion.div
              className="flex justify-center cursor-pointer"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 1.5 }}
              onClick={handleInteraction}
            >
              <img
                src={interactionState.lit ? ASSETS.intro.candleLit.path : ASSETS.intro.candleUnlit.path}
                alt="Candle"
                className="w-24 h-auto"
              />
              {!interactionState.lit && (
                <div className="absolute mt-32 text-center text-sm text-gray-400 animate-pulse">{interactionState.prompt}</div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showBreath && (
            <motion.img
              src={(ASSETS.effects as any).breath.path}
              alt="Breath"
              className="absolute bottom-0 left-0 w-full h-auto pointer-events-none"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: [0, 0.4, 0.4, 0], y: 0 }}
              transition={{ duration: 4, times: [0, 0.2, 0.8, 1] }}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showAsset && (
            <motion.div
              className="absolute pointer-events-none"
              style={showAsset.position}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 0.6, scale: 1, x: showAsset.subtle_move ? [0, 5, -5, 0] : 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2, x: { duration: 10, repeat: Infinity, repeatType: 'mirror' } }}
            >
              <img src={(ASSETS.introEntities as any)[showAsset.type].path} alt={showAsset.type} className="w-32 h-auto" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Skip button */}
      <AnimatePresence>
        {showSkip && (
          <motion.button
            onClick={handleSkip}
            className="absolute top-4 right-4 px-4 py-2 text-sm bg-black/50 hover:bg-black/70 border border-white/20 rounded transition-colors"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            Skip Intro
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

const DoorTransition = ({
  onComplete,
  getSfx,
  playWhisper,
  volumeSettings,
}: {
  onComplete: () => void;
  getSfx: (name: keyof typeof ASSETS.sfx) => Howl;
  playWhisper: () => void;
  volumeSettings: VolumeSettings;
}) => {
  const [phase, setPhase] = useState('start'); // 'start', 'doorVisible', 'doorOpening', 'zooming'
  const transitionAmbientRef = useRef<Howl | null>(null);

  useEffect(() => {
    const ambient = new Howl({
      src: [ASSETS.sfx.introAmbient.path],
      loop: true,
      volume: volumeSettings.muted ? 0 : volumeSettings.master * volumeSettings.music * 0.7,
    });
    ambient.play();
    transitionAmbientRef.current = ambient;

    const timings = CONSTANTS.doorTransition;
    const t1 = setTimeout(() => setPhase('doorVisible'), timings.doorAppearDelay);
    const t2 = setTimeout(() => {
      getSfx('doorCreak').play();
      playWhisper();
      setPhase('doorOpening');
    }, timings.doorAppearDelay + timings.doorOpenDelay);
    const t3 = setTimeout(() => setPhase('zooming'), timings.doorAppearDelay + timings.doorOpenDelay + timings.zoomInDelay);
    const t4 = setTimeout(() => {
      const masterVol = volumeSettings.muted ? 0 : volumeSettings.master;
      transitionAmbientRef.current?.fade(masterVol * volumeSettings.music * 0.7, 0, 500);
      setTimeout(onComplete, 500);
    }, timings.totalDuration - 500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      transitionAmbientRef.current?.stop();
      transitionAmbientRef.current?.unload();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.img
        src={ASSETS.backgrounds.menuHallway.path}
        className="absolute inset-0 w-full h-full object-cover"
        alt=""
        initial={{ scale: 1.03 }}
        animate={phase === 'zooming' ? { scale: 4, opacity: 0 } : { scale: 1.03 }}
        transition={{ duration: 0.8, ease: 'easeIn' }}
      />
      <motion.div
        animate={phase === 'zooming' ? { scale: 15, opacity: 0 } : { scale: 1, opacity: 1 }}
        transition={{ duration: 1.5, ease: 'easeIn' }}
      >
        <AnimatePresence>
          {phase !== 'start' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
              className="relative w-[250px] h-[450px] lg:w-[300px] lg:h-[540px]"
            >
              <img
                src={ASSETS.ui.creepyDoorOpen.path}
                alt=""
                className="absolute inset-0 w-full h-full object-contain"
              />
              <AnimatePresence>
                {(phase === 'doorOpening' || phase === 'zooming') && (
                  <motion.img
                    src={ASSETS.effects.doorFog.path}
                    alt=""
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 0.6, y: -30 }}
                    transition={{ duration: 2, delay: 0.5, ease: 'circOut' }}
                    className="absolute inset-0 w-full h-full object-contain mix-blend-screen"
                  />
                )}
              </AnimatePresence>
              <motion.img
                src={ASSETS.ui.creepyDoorClosed.path}
                alt="A creepy door"
                animate={
                  phase === 'doorOpening' || phase === 'zooming'
                    ? { rotateY: -110, originX: 0 }
                    : { rotateY: 0, originX: 0 }
                }
                transition={{ duration: 2, ease: 'easeInOut' }}
                className="absolute inset-0 w-full h-full object-contain"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

function App() {
  const isMobile = useIsMobile();
  const [gameState, setGameState] = useState<GameState>('mainMenu');
  const [showSettings, setShowSettings] = useState<boolean>(false);

  const [volumeSettings, setVolumeSettings] = usePersistentState<VolumeSettings>('udch_volume', {
    master: CONSTANTS.volume.defaultMaster,
    sfx: CONSTANTS.volume.defaultSfx,
    music: CONSTANTS.volume.defaultMusic,
    muted: CONSTANTS.volume.defaultMuted,
    brightness: CONSTANTS.volume.defaultBrightness,
  });

  const [playerName, setPlayerName] = usePersistentState<string>('udch_player_name', '');
  const [nameInput, setNameInput] = useState<string>('');
  const [nameAsked, setNameAsked] = useState<boolean>(false);

  const [discovered, setDiscovered] = usePersistentState<string[]>(
    'udch_discovered',
    CONSTANTS.initialElements.slice()
  );
  const [history, setHistory] = usePersistentState<ComboHistoryItem[]>('udch_history', []);
  const [chapter, setChapter] = usePersistentState<number>('udch_chapter', 0);

  const [doorLocation, setDoorLocation] = useState<DoorLocation>('center');
  const [lightLevel, setLightLevel] = useState<number>(1);
  const [reflectionActive, setReflectionActive] = useState<boolean>(false);
  const [holyShitTriggered, setHolyShitTriggered] = usePersistentState<boolean>('udch_holy', false);
  const [ambientOn, setAmbientOn] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  const [realityDrift, setRealityDrift] = usePersistentState<number>('udch_realityDrift', 0);
  
  const [backgrounds, setBackgrounds] = usePersistentState<{ current: string; previous: string | null }>('udch_backgrounds', {
    current: ASSETS.backgrounds.houseRoom.path,
    previous: null,
  });

  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [tooltip, setTooltip] = useState<{ name: string; x: number; y: number } | null>(null);
  const [historyPage, setHistoryPage] = useState<number>(0);
  const [gameLog, setGameLog] = useState<string[]>([]);
  const [logVisible, setLogVisible] = useState<boolean>(false);
  const logTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [elementArt, setElementArt] = usePersistentState<Record<string, string>>('udch_element_art', {});
  const [narrative, setNarrative] = usePersistentState<string[]>('udch_narrative', [CONSTANTS.narrative.initial]);

  // New state for tracking used narratives
  const [usedNarratives, setUsedNarratives] = usePersistentState<Record<string, number>>('udch_used_narratives', {});

  // State to track the last logo variant from main menu
  const [lastLogoVariant, setLastLogoVariant] = useState<number>(0);

  const ambientRef = useRef<Howl | null>(null);
  const ambientTenseRef = useRef<Howl | null>(null);
  const endThemeRef = useRef<Howl | null>(null);
  const sfxRef = useRef<Record<string, Howl>>({});
  
  // New state for drag-and-drop
  const [placedElements, setPlacedElements] = usePersistentState<PlacedElement[]>('udch_placed_elements', []);
  const [enlargedElementDisplay, setEnlargedElementDisplay] = useState<{ name: string; x: number; y: number; id: number } | null>(null);
  const nextId = useRef(0);
  const sceneRef = useRef<HTMLDivElement>(null);

  // New state for dynamic background system
  const [activeIntrusion, setActiveIntrusion] = useState<any | null>(null);
  const intrusionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [backgroundShift, setBackgroundShift] = useState<{ filter: string; narrative: string } | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeJumpscare, setActiveJumpscare] = useState<string | null>(null);
  const [backgroundEntities, setBackgroundEntities] = usePersistentState<string[]>('udch_bg_entities', []);
  const [temporaryEffects, setTemporaryEffects] = useState<TemporaryEffect[]>([]);

  // New state for narrative effects
  const [narrativeEffects, setNarrativeEffects] = useState<NarrativeEffect[]>([]);

  // New state for story-specific entities
  const [guestActive, setGuestActive] = useState<boolean>(false);
  const [shadowCompanionActive, setShadowCompanionActive] = useState<boolean>(false);
  const [isDoorAjar, setIsDoorAjar] = usePersistentState<boolean>('udch_door_ajar', false);

  // New state for main menu transition
  const [transitionTo, setTransitionTo] = useState<'intro' | 'playing' | null>(null);

  // New state for mobile crafting
  const [craftingSlots, setCraftingSlots] = useState<(string | null)[]>([null, null]);
  const longPressTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Inactivity timer
  const lastInteractionTime = useRef<number>(Date.now());

  // New state for end screen story modal
  const [showFullStory, setShowFullStory] = useState(false);

  // New state for mobile context menu
  const [mobileContextMenu, setMobileContextMenu] = useState<{ name: string; x: number; y: number; target: 'inventory' | 'scene'; id?: number } | null>(null);
  const isLongPress = useRef(false);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  // Tutorial State
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialDiscovered, setTutorialDiscovered] = useState(CONSTANTS.tutorial.initialElements);
  const [tutorialPlaced, setTutorialPlaced] = useState<PlacedElement[]>([]);
  const [tutorialNarrative, setTutorialNarrative] = useState<string[]>([CONSTANTS.tutorial.initialNarrative]);
  const [tutorialActionCompleted, setTutorialActionCompleted] = useState(false);
  const [highlightNewGame, setHighlightNewGame] = useState(false);

  const isTutorial = gameState === 'tutorial';

  // Refs for highlighting UI elements in tutorial
  const discoveriesPanelRef = useRef<HTMLDivElement>(null);
  const craftingSlotsRef = useRef<HTMLDivElement>(null);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const driftMeterRef = useRef<HTMLDivElement>(null);

  // Derived
  const discoveredSet = useMemo(() => new Set(discovered), [discovered]);

  const filteredElements = useMemo(() => {
    const source = isTutorial ? tutorialDiscovered : discovered;
    if (!search.trim()) return source;
    const q = search.toLowerCase();
    return source.filter((e) => e.toLowerCase().includes(q));
  }, [discovered, search, isTutorial, tutorialDiscovered]);

  const hasSavedGame = useMemo(() => {
    return history.length > 0 || chapter > 0 || discovered.length > CONSTANTS.initialElements.length;
  }, [history.length, chapter, discovered.length]);

  // Calculate gameplay boundary - now uses full scene area
  const gameplayBoundary = useMemo(() => {
    if (!sceneRef.current) return CONSTANTS.dragBoundary.fullScene;
    
    const rect = sceneRef.current.getBoundingClientRect();
    return {
      left: 0,
      top: 0,
      right: rect.width,
      bottom: rect.height,
    };
  }, []);

  // Apply volume settings to audio
  const applyVolumeToAudio = useCallback(() => {
    const masterVol = volumeSettings.muted ? 0 : volumeSettings.master;

    if (ambientRef.current) {
      ambientRef.current.volume(masterVol * volumeSettings.music);
    }
    if (ambientTenseRef.current) {
      ambientTenseRef.current.volume(masterVol * volumeSettings.music);
    }
    if (endThemeRef.current) {
      endThemeRef.current.volume(masterVol * volumeSettings.music);
    }

    Object.values(sfxRef.current).forEach((howl) => {
      howl.volume(masterVol * volumeSettings.sfx);
    });
  }, [volumeSettings]);

  useEffect(() => {
    applyVolumeToAudio();
  }, [applyVolumeToAudio]);

  const addLogMessage = useCallback((message: string) => {
    setGameLog((prev) => [message, ...prev].slice(0, 3));
    setLogVisible(true);
    if (logTimerRef.current) {
      clearTimeout(logTimerRef.current);
    }
    logTimerRef.current = setTimeout(() => {
      setLogVisible(false);
    }, 5000);
  }, []);

  const triggerTemporaryEffect = useCallback((id: string, duration: number) => {
    const key = Date.now() + Math.random();
    setTemporaryEffects(prev => [...prev, { id, key, duration }]);
    setTimeout(() => {
      setTemporaryEffects(prev => prev.filter(e => e.key !== key));
    }, duration);
  }, []);

  const triggerNarrativeEffect = useCallback((effectId: string) => {
    const effectData = (CONSTANTS.narrativeEffects as any)[effectId];
    if (!effectData) return;

    const key = Date.now() + Math.random();
    const effect: NarrativeEffect = {
      id: effectId,
      key,
      type: effectData.type,
      duration: effectData.duration,
      pulses: effectData.pulses,
    };

    setNarrativeEffects(prev => [...prev, effect]);

    setTimeout(() => {
      setNarrativeEffects(prev => prev.filter(e => e.key !== key));
    }, effectData.duration * (effectData.pulses || 1));
  }, []);

  const integrateNarrative = useCallback(
    (text: string, key: string, options?: { once?: boolean }) => {
      // In tutorial mode, just append to the tutorial narrative
      if (isTutorial) {
        setTutorialNarrative(prev => [...prev, text]);
        addLogMessage(text.split(/[.!?]/)[0] + '.');
        return;
      }

      // Check if this narrative has been used before
      const narrativeKey = text.toLowerCase().trim();
      const usageCount = usedNarratives[narrativeKey] || 0;
      
      if (options?.once && usageCount > 0) {
        addLogMessage("The familiar sensation returns...");
        return;
      }
      
      // Refine text using the AI narrator. The seed is modified by usageCount to create variations on repeated narratives.
      const refinedText = refineNarrativeText(text, simpleHash(narrativeKey) + usageCount, realityDrift);
      
      // Apply drift-based tone
      const finalText = applyNarrativeTone(refinedText, realityDrift);

      // Update usage count
      setUsedNarratives(prev => ({
        ...prev,
        [narrativeKey]: usageCount + 1
      }));

      const rng = makeRng(simpleHash(key));
      const choice = rng.next();

      setNarrative((prev) => {
        let newNarrative = [...prev];
        const lastParagraph = newNarrative[newNarrative.length - 1] || '';

        // STRATEGY 1: MERGE (45% chance, if last paragraph is not too long and we have paragraphs)
        if (choice < 0.45 && newNarrative.length > 0 && lastParagraph.length < 450) {
          const separator = /[.!?]$/.test(lastParagraph.trim()) ? ' ' : '. ';
          newNarrative[newNarrative.length - 1] = lastParagraph + separator + finalText;
          return newNarrative;
        }
        // STRATEGY 2: REWRITE (15% chance, if story is long enough)
        else if (choice < 0.60 && newNarrative.length > 3) {
          const rewriteIndex = Math.floor(rng.next() * (newNarrative.length - 2)) + 1; // Don't rewrite the intro or the very last paragraph
          const original = newNarrative[rewriteIndex];
          const connector = rng.pick(CONSTANTS.ai.rewriteConnectors);
          const rewritten = connector
            .replace('{original}', original.toLowerCase().replace(/^[a-z]\s/, ''))
            .replace('{new}', finalText.toLowerCase());
          newNarrative[rewriteIndex] = titleCaseName(rewritten.charAt(0)) + rewritten.slice(1);
          return newNarrative;
        }
        // STRATEGY 3: INSERT (15% chance, if story is long enough)
        else if (choice < 0.75 && newNarrative.length > 1) {
          const insertIndex = newNarrative.length - 1;
          newNarrative.splice(insertIndex, 0, finalText);
          return newNarrative;
        }
        // STRATEGY 4: APPEND (25% chance, default)
        else {
          newNarrative.push(finalText);
          return newNarrative;
        }
      });

      const firstSentence = finalText.split(/[.!?]/)[0];
      addLogMessage((firstSentence ? firstSentence : finalText).trim() + '.');

      // Check for narrative cues to trigger background effects
      const lowerText = finalText.toLowerCase();
      for (const [cue, effect] of Object.entries(CONSTANTS.narrativeCues)) {
        if (lowerText.includes(cue)) {
          triggerNarrativeEffect(effect.effect);
        }
      }
    },
    [addLogMessage, setNarrative, triggerNarrativeEffect, usedNarratives, setUsedNarratives, realityDrift, isTutorial, setTutorialNarrative]
  );

  const ensureAmbient = useCallback(() => {
    if (!ambientRef.current) {
      ambientRef.current = new Howl({
        src: [ASSETS.music.ambientBasement.path],
        loop: true,
        volume: volumeSettings.muted ? 0 : volumeSettings.master * volumeSettings.music,
      });
    }
    if (!ambientTenseRef.current) {
      ambientTenseRef.current = new Howl({
        src: [ASSETS.music.ambientTense.path],
        loop: true,
        volume: 0, // Start muted
      });
    }
  }, [volumeSettings]);

  const playAmbient = useCallback(() => {
    ensureAmbient();
    if (!ambientRef.current || !ambientTenseRef.current) return;
    if (!ambientRef.current.playing()) {
      ambientRef.current.play();
    }
    if (!ambientTenseRef.current.playing()) {
      ambientTenseRef.current.play();
    }
    setAmbientOn(true);
  }, [ensureAmbient]);

  const stopAmbient = useCallback(() => {
    if (ambientRef.current && ambientRef.current.playing()) {
      ambientRef.current.stop();
    }
    if (ambientTenseRef.current && ambientTenseRef.current.playing()) {
      ambientTenseRef.current.stop();
    }
    setAmbientOn(false);
  }, []);

  const getSfx = useCallback(
    (name: keyof typeof ASSETS.sfx) => {
      if (sfxRef.current[name as string]) return sfxRef.current[name as string];
      const baseVolume =
        name === 'combineFail'
          ? 0.3
          : name === 'combineSuccess'
          ? 0.5
          : name === 'elementAppear'
          ? 0.6
          : name === 'doorCreak'
          ? 0.8
          : name === 'heartbeat'
          ? 0.6
          : name === 'jumpscare1'
          ? 1.0
          : name === 'jumpscare2'
          ? 1.0
          : name === 'doorAppear'
          ? 0.7
          : name === 'realityShift'
          ? 0.6
          : name === 'mirrorBreak'
          ? 0.8
          : name === 'candleLight'
          ? 0.4
          : name === 'introAmbient'
          ? 0.3
          : name === 'driftIncrease'
          ? 0.4
          : name === 'driftDecrease'
          ? 0.5
          : name === 'inactivityPulse'
          ? 0.6
          : 0.8;

      const howl = new Howl({
        src: [ASSETS.sfx[name].path],
        volume: volumeSettings.muted ? 0 : volumeSettings.master * volumeSettings.sfx * baseVolume,
        loop: name === 'heartbeat' || name === 'introAmbient',
      });
      sfxRef.current[name as string] = howl;
      return howl;
    },
    [volumeSettings]
  );

  const playWhisper = useCallback(
    (text?: string) => {
      const whisper = getSfx('whisper' as any);
      const pan = Math.random() < 0.5 ? -0.8 : 0.8;
      try {
        // @ts-ignore: stereo exists on howler at runtime
        whisper.stereo(pan);
      } catch {}
      whisper.play();
      if (text) addLogMessage(text);
    },
    [getSfx, addLogMessage]
  );

  const playFlicker = useCallback(() => {
    getSfx('flicker' as any).play();
  }, [getSfx]);

  const playDoor = useCallback(() => {
    getSfx('doorCreak' as any).play();
  }, [getSfx]);

  const playHeartbeat = useCallback(() => {
    getSfx('heartbeat' as any).play();
  }, [getSfx]);

  const stopHeartbeat = useCallback(() => {
    const hb = sfxRef.current['heartbeat'];
    if (hb && hb.playing()) hb.stop();
  }, []);

  const playCombineSuccess = useCallback(() => {
    getSfx('combineSuccess' as any).play();
  }, [getSfx]);

  const playCombineFail = useCallback(() => {
    getSfx('combineFail' as any).play();
    if (isTutorial) return;
    if (Math.random() < 0.3) playFlicker();
    if (Math.random() < 0.15) playWhisper();
  }, [getSfx, playFlicker, playWhisper, isTutorial]);

  const playElementAppear = useCallback(() => {
    getSfx('elementAppear' as any).play();
  }, [getSfx]);

  const playDoorAppear = useCallback(() => {
    getSfx('doorAppear' as any).play();
  }, [getSfx]);

  const playRealityShift = useCallback(() => {
    getSfx('realityShift' as any).play();
  }, [getSfx]);

  const playMirrorBreak = useCallback(() => {
    getSfx('mirrorBreak' as any).play();
  }, [getSfx]);

  const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
  const bumpRealityDrift = useCallback(
    (delta: number) => {
      if (isTutorial) return;
      setRealityDrift((c) => {
        const newValue = clamp(c + delta, 0, CONSTANTS.realityDrift.max);
        if (newValue >= 100) {
          setGameState('realityCollapse');
        }
        return newValue;
      });
    },
    [setRealityDrift, isTutorial]
  );

  const handleFullEnvironmentChange = useCallback((newBgPath: string) => {
    const jumpscareRoll = Math.random();
    if (jumpscareRoll < CONSTANTS.dynamicBackground.jumpscares.chanceOnCritical) {
      const jumpscare = randomChoice(CONSTANTS.dynamicBackground.jumpscares.list);
      getSfx(jumpscare.sound as any).play();
      
      setIsTransitioning(true);
      setActiveJumpscare(jumpscare.asset);
  
      setTimeout(() => {
        setActiveJumpscare(null);
        setIsTransitioning(false);
      }, CONSTANTS.dynamicBackground.jumpscares.duration);
    }
    
    setBackgrounds(prev => ({ current: newBgPath, previous: prev.current }));

    // Background-aware narrative
    const bgKey = Object.keys(ASSETS.backgrounds).find(
      key => (ASSETS.backgrounds as any)[key].path === newBgPath
    );

    if (bgKey && (CONSTANTS.backgroundNarratives as any)[bgKey]) {
      const narratives = (CONSTANTS.backgroundNarratives as any)[bgKey];
      const narrative = randomChoice(narratives);
      // Use a unique key for these narratives to avoid collision
      integrateNarrative(narrative, `bg_change_${bgKey}_${Date.now()}`, { once: true });
    }
  }, [getSfx, setBackgrounds, integrateNarrative]);

  const handleIntrusionMissed = useCallback(() => {
    if (!activeIntrusion) return;
    const narrative = CONSTANTS.dynamicBackground.intrusions.clickFailNarrative;
    integrateNarrative(narrative, `miss_${activeIntrusion.id}`, { once: true });
    setActiveIntrusion(null);
    intrusionTimerRef.current = null;
  }, [activeIntrusion, integrateNarrative]);

  const triggerIntrusion = useCallback(() => {
    if (activeIntrusion || isTransitioning) return;

    const intrusion = randomChoice(CONSTANTS.dynamicBackground.intrusions.list);
    setActiveIntrusion(intrusion);
    playFlicker();

    if (intrusionTimerRef.current) clearTimeout(intrusionTimerRef.current);
    intrusionTimerRef.current = setTimeout(handleIntrusionMissed, CONSTANTS.dynamicBackground.intrusions.duration);
  }, [activeIntrusion, isTransitioning, playFlicker, handleIntrusionMissed]);

  const triggerSubtleShift = useCallback(() => {
    if (isTransitioning) return;
    const shift = randomChoice(CONSTANTS.dynamicBackground.subtleShifts.shifts);
    setBackgroundShift(shift);
    integrateNarrative(shift.narrative, `shift_${shift.id}`, { once: true });
  }, [isTransitioning, integrateNarrative, setBackgroundShift]);

  const triggerDynamicBackgroundEvent = useCallback((context: 'combine' | 'chapter' | 'corruption_tick') => {
    if (isTutorial) return;
    const rand = Math.random();
  
    if (context === 'chapter') {
      if (Math.random() < CONSTANTS.dynamicBackground.subtleShifts.chanceOnChapterAdvance) {
        triggerSubtleShift();
      }
      return;
    }
  
    if (context === 'corruption_tick' && realityDrift > 40) {
      if (rand < CONSTANTS.dynamicBackground.intrusions.chanceOnCorruptionTick) {
        triggerIntrusion();
      } else if (realityDrift > 60 && rand < (CONSTANTS.dynamicBackground.intrusions.chanceOnCorruptionTick + CONSTANTS.dynamicBackground.subtleShifts.chanceOnHighCorruption)) {
        triggerSubtleShift();
      }
      return;
    }
  
    if (context === 'combine') {
      if (rand < CONSTANTS.dynamicBackground.intrusions.chanceOnCombine) {
        triggerIntrusion();
      }
    }
  }, [realityDrift, triggerIntrusion, triggerSubtleShift, isTutorial]);

  const tryAdvanceChapterFor = useCallback(
    (result: string) => {
      if (isTutorial) return;
      const chapterGates = CONSTANTS.chapterGates as Record<string, number>;
      const newChapter = chapterGates[result];

      if (newChapter !== undefined && chapter < newChapter) {
        setChapter(newChapter);
        playDoor();
        addLogMessage(`Chapter ${newChapter}: The story deepens...`);
        triggerDynamicBackgroundEvent('chapter');

        switch (newChapter) {
          case 1:
            setLightLevel(0.8);
            playWhisper('Don\'t open the door.');
            playDoorAppear();
            break;
          case 2:
            playRealityShift();
            playHeartbeat();
            addLogMessage('Reality begins to shift...');
            break;
          case 3:
            handleFullEnvironmentChange(ASSETS.backgrounds.dampCorridors.path);
            setLightLevel(0.7);
            break;
          case 4:
            handleFullEnvironmentChange(ASSETS.backgrounds.mirrorHallway.path);
            playMirrorBreak();
            setReflectionActive(true);
            break;
          case 5:
            setGuestActive(true);
            playWhisper('You never go back up.');
            break;
          case 6:
            playFlicker();
            addLogMessage('The door begins to move...');
            break;
          case 7:
            setShadowCompanionActive(true);
            stopHeartbeat();
            addLogMessage('Something walks with you now.');
            break;
          case 8:
            playWhisper("The photos are not memories. They are warnings.");
            addLogMessage("The story is not what you remember.");
            break;
        }
      }
    },
    [chapter, playDoor, playFlicker, playHeartbeat, playWhisper, setChapter, stopHeartbeat, addLogMessage, handleFullEnvironmentChange, playDoorAppear, playRealityShift, playMirrorBreak, triggerDynamicBackgroundEvent, isTutorial]
  );

  useEffect(() => {
    if (gameState !== 'playing' || isTutorial) return;
    const id = setInterval(() => {
      const which = (['left', 'center', 'right'] as DoorLocation[])[Math.floor(Math.random() * 3)];
      setDoorLocation(which);
      const newLight = Math.min(1, Math.max(0.6, lightLevel + (Math.random() - 0.5) * 0.2));
      setLightLevel(newLight);
      if (Math.random() < 0.2) {
        playFlicker();
      }
    }, getRandomInt(12000, 22000));
    return () => clearInterval(id);
  }, [lightLevel, playFlicker, gameState, isTutorial]);

  useEffect(() => {
    if (gameState === 'playing' && !isTutorial && !playerName && !nameAsked) {
      setNameAsked(true);
    }
  }, [playerName, nameAsked, gameState, isTutorial]);

  const maybeDropLore = useCallback(
    (hint?: 'afterSuccess' | 'timer') => {
      if (isTutorial) return;
      const p =
        hint === 'timer'
          ? 0.3
          : 0.18 + (realityDrift >= 50 ? 0.08 : 0) + (chapter >= 5 ? 0.05 : 0);
      if (Math.random() > p) return;

      const pools: string[] = [];
      if (!CONSTANTS.loreFragments || !CONSTANTS.loreFragments.general) {
        throw new Error('loreFragments.general missing');
      }
      pools.push(...CONSTANTS.loreFragments.general);
      if (realityDrift >= 50) {
        if (!CONSTANTS.loreFragments.highCorruption) {
          throw new Error('loreFragments.highCorruption missing');
        }
        pools.push(...CONSTANTS.loreFragments.highCorruption);
      } else {
        if (!CONSTANTS.loreFragments.lowCorruption) {
          throw new Error('loreFragments.lowCorruption missing');
        }
        pools.push(...CONSTANTS.loreFragments.lowCorruption);
      }
      const chapterSpecific =
        CONSTANTS.loreFragments.chapters &&
        (CONSTANTS.loreFragments.chapters as any)[String(chapter)];
      if (chapterSpecific && chapterSpecific.length) pools.push(...chapterSpecific);

      if (CONSTANTS.narrative.hints) {
        pools.push(...CONSTANTS.narrative.hints);
      }

      if (pools.length === 0) {
        throw new Error('No lore pools available to drop from');
      }

      const text = randomChoice(pools);
      setHistory((prev) => [{ a: '', b: '', result: '—', chapter, text }, ...prev].slice(0, 200));
      addLogMessage(text);
    },
    [chapter, realityDrift, setHistory, addLogMessage, isTutorial]
  );

  const onCombine = useCallback(
    (a: string, b: string): CombineResult => {
      if (isTutorial) {
        const tutorialSteps = CONSTANTS.tutorial.steps.filter(s => isMobile ? !s.isDesktopOnly : !s.isMobileOnly);
        const stepConfig = tutorialSteps[tutorialStep];
        if (stepConfig.action === 'combine') {
            const requiredKey = normalizeKey(stepConfig.required[0], stepConfig.required[1]);
            const givenKey = normalizeKey(a, b);
            if (requiredKey === givenKey) {
                playCombineSuccess();
                setTutorialDiscovered(prev => [...new Set([...prev, stepConfig.result])]);
                integrateNarrative(CONSTANTS.tutorial.exampleNarrative, 'tutorial_combo');
                setTutorialActionCompleted(true);
                addLogMessage(`New Discovery: ${stepConfig.result}`);
                return { type: 'element', name: stepConfig.result, isNew: true };
            } else {
                playCombineFail();
                addLogMessage(`That's not the right combination. Try combining '${stepConfig.required[0]}' and '${stepConfig.required[1]}'.`);
                return { type: 'fail' };
            }
        }
        return { type: 'fail' };
      }

      if (a === b) return { type: 'fail' };
      const key = normalizeKey(a, b);
      const comboData = (CONSTANTS.combinations as any)[key];

      if (!comboData) {
        playCombineFail();
        getSfx('driftIncrease').play();
        bumpRealityDrift(CONSTANTS.realityDrift.failGain);
        return { type: 'fail' };
      }

      const isNewDiscovery = !discoveredSet.has(comboData.result) && comboData.type === 'element';
      const isNewEvent = comboData.type !== 'element' && (usedNarratives[comboData.narrative.toLowerCase().trim()] || 0) === 0;
      const isNew = isNewDiscovery || isNewEvent;

      if (isNew) {
        bumpRealityDrift(CONSTANTS.realityDrift.newDiscoveryLoss);
        getSfx('driftDecrease').play();
      } else {
        bumpRealityDrift(CONSTANTS.realityDrift.oldDiscoveryLoss);
        playCombineSuccess();
      }

      if (comboData.narrative) {
        const isBackgroundAction = comboData.type === 'background_event' || comboData.type === 'special_event' || comboData.type === 'environment_change';
        integrateNarrative(comboData.narrative, key, { once: isBackgroundAction });
      }
      
      setHistory((prev) => [{ a, b, result: comboData.result || 'Event', chapter, text: comboData.narrative }, ...prev].slice(0, 200));
      triggerDynamicBackgroundEvent('combine');
      maybeDropLore('afterSuccess');

      // Random chance for background change on any successful combo
      if (Math.random() < CONSTANTS.realityDrift.backgroundChangeChanceOnCombine) {
        const unsettlingBgs = CONSTANTS.realityDrift.unsettlingBackgrounds;
        const newBgKey = randomChoice(unsettlingBgs);
        const newBgPath = (ASSETS.backgrounds as any)[newBgKey]?.path;
        if (newBgPath) {
          handleFullEnvironmentChange(newBgPath);
          addLogMessage("The house shifts around you...");
        }
      }

      if (comboData.type === 'element') {
        const resultName = comboData.result;
        
        if (isNewDiscovery) {
          setDiscovered((prev) => [...prev, resultName]);
          setElementArt((prev) => (prev[resultName] ? prev : { ...prev, [resultName]: getAiImageUrl(resultName) }));
          playElementAppear();
          addLogMessage(`New Discovery: ${resultName}`);

          // Story-specific triggers for elements
          if (resultName === 'The Door That Wasn\'t There') playDoorAppear();
          else if (resultName === 'First Descent') playDoor();
          else if (resultName === 'Moving Reflection') setReflectionActive(true);
          else if (resultName === 'The Guest') setGuestActive(true);
          else if (resultName === 'Shadow Companion') setShadowCompanionActive(true);

          tryAdvanceChapterFor(resultName);
        }
        return { type: 'element', name: resultName, isNew: isNewDiscovery };
      }
      
      if (comboData.type === 'background_event') {
        if (comboData.duration === 'permanent') {
          setBackgroundEntities(prev => [...new Set([...prev, comboData.id])]);
        } else {
          triggerTemporaryEffect(comboData.id, comboData.duration);
        }
        return { type: 'background', isNew };
      }

      if (comboData.type === 'environment_change') {
        const bgAsset = (ASSETS.backgrounds as any)[comboData.background];
        if (bgAsset) {
          handleFullEnvironmentChange(bgAsset.path);
        }
        return { type: 'background', isNew };
      }

      if (comboData.type === 'special_event') {
        if (comboData.id === 'door_ajar') {
            setIsDoorAjar(true);
        }
        if (comboData.id === 'true_ending') {
          setGameState('gameEnd');
        }
        return { type: 'background', isNew };
      }

      // Fallback for misconfigured types
      playCombineFail();
      return { type: 'fail' };
    },
    [
      isTutorial,
      tutorialStep,
      isMobile,
      setTutorialActionCompleted,
      setTutorialDiscovered,
      setTutorialNarrative,
      chapter,
      discoveredSet,
      playCombineSuccess,
      playCombineFail,
      playDoor,
      playElementAppear,
      playDoorAppear,
      setDiscovered,
      setHistory,
      tryAdvanceChapterFor,
      bumpRealityDrift,
      maybeDropLore,
      addLogMessage,
      integrateNarrative,
      setElementArt,
      triggerDynamicBackgroundEvent,
      setBackgroundEntities,
      triggerTemporaryEffect,
      setIsDoorAjar,
      handleFullEnvironmentChange,
      getSfx,
      usedNarratives
    ]
  );

  const checkForCombination = useCallback((movedElementId: number, currentElements: PlacedElement[]) => {
    if (isMobile) return;
    const movedElement = currentElements.find(p => p.id === movedElementId);
    if (!movedElement) return;

    for (const otherElement of currentElements) {
        if (movedElement.id === otherElement.id) continue;

        const dx = movedElement.x - otherElement.x;
        const dy = movedElement.y - otherElement.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < COMBINATION_DISTANCE) {
            const result = onCombine(movedElement.name, otherElement.name);
            
            if (result.type !== 'fail') {
                const elementsAfterRemoval = currentElements.filter(p => p.id !== movedElement.id && p.id !== otherElement.id);

                if (isTutorial) {
                    setTutorialPlaced(elementsAfterRemoval);
                } else {
                    if (result.type === 'element') {
                        const midX = (movedElement.x + otherElement.x) / 2;
                        const midY = (movedElement.y + otherElement.y) / 2;
                        const newCombinedElement: PlacedElement = {
                            id: nextId.current++,
                            name: result.name,
                            x: midX,
                            y: midY,
                            anchoredUntil: Date.now() + CONSTANTS.realityDrift.elementMovement.anchorDuration,
                        };
                        setPlacedElements([...elementsAfterRemoval, newCombinedElement]);
                    } else { // Background event, just remove the elements
                        setPlacedElements(elementsAfterRemoval);
                    }
                }
                return; // Combination happened, exit loop
            }
        }
    }
  }, [onCombine, isMobile, setPlacedElements, isTutorial, setTutorialPlaced]);

  // Constrain position to gameplay boundary
  const constrainPosition = useCallback((x: number, y: number, elementWidth: number, elementHeight: number) => {
    return {
      x: Math.max(gameplayBoundary.left, Math.min(gameplayBoundary.right - elementWidth, x)),
      y: Math.max(gameplayBoundary.top, Math.min(gameplayBoundary.bottom - elementHeight, y))
    };
  }, [gameplayBoundary]);

  const handleDropOnScene = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (isMobile || !sceneRef.current) return;

    if (!e.dataTransfer.types.includes('application/x-udch-element')) {
      addLogMessage(CONSTANTS.narrative.invalidDrop);
      return;
    }

    const name = e.dataTransfer.getData('text/plain');
    const currentDiscoveries = isTutorial ? new Set(tutorialDiscovered) : discoveredSet;
    if (!name || !currentDiscoveries.has(name)) {
      addLogMessage(CONSTANTS.narrative.invalidDrop);
      return;
    }

    const sceneRect = sceneRef.current.getBoundingClientRect();
    const rawX = e.clientX - sceneRect.left;
    const rawY = e.clientY - sceneRect.top;

    const animationId = nextId.current++;

    const constrainedPos = constrainPosition(rawX, rawY, 0, 0);

    setEnlargedElementDisplay({
      id: animationId,
      name,
      x: constrainedPos.x,
      y: constrainedPos.y,
    });

    setTimeout(() => {
      const newElement: PlacedElement = {
        id: animationId,
        name,
        x: constrainedPos.x - PLACED_ELEMENT_SIZE / 2,
        y: constrainedPos.y - PLACED_ELEMENT_SIZE / 2,
      };

      const stateSetter = isTutorial ? setTutorialPlaced : setPlacedElements;
      const elementList = isTutorial ? tutorialPlaced : placedElements;

      stateSetter(prev => {
        const updated = [...prev, newElement];
        setTimeout(() => checkForCombination(newElement.id, updated), 50);
        return updated;
      });

      setEnlargedElementDisplay(null);
    }, CONSTANTS.ui.enlargedDisplayDuration);
  }, [checkForCombination, addLogMessage, discoveredSet, constrainPosition, isMobile, setPlacedElements, isTutorial, tutorialDiscovered, setTutorialPlaced, tutorialPlaced, placedElements]);

  const handleElementDragEnd = useCallback((draggedId: number, info: { offset: { x: number; y: number } }, elementRef: HTMLDivElement | null) => {
    if (isMobile || !sceneRef.current || !elementRef) return;
    
    const stateSetter = isTutorial ? setTutorialPlaced : setPlacedElements;

    stateSetter(prev => {
        const currentElement = prev.find(p => p.id === draggedId);
        if (!currentElement) return prev;

        const newX = currentElement.x + info.offset.x;
        const newY = currentElement.y + info.offset.y;

        const { width, height } = elementRef.getBoundingClientRect();
        const constrainedPos = constrainPosition(newX, newY, width, height);

        const updated = prev.map(p => 
            p.id === draggedId ? { ...p, x: constrainedPos.x, y: constrainedPos.y } : p
        );
        
        setTimeout(() => checkForCombination(draggedId, updated), 50);
        return updated;
    });
  }, [checkForCombination, constrainPosition, isMobile, setPlacedElements, isTutorial, setTutorialPlaced]);

  const handleIntrusionClick = useCallback(() => {
    if (!activeIntrusion) return;
    if (intrusionTimerRef.current) clearTimeout(intrusionTimerRef.current);
    
    const narrative = CONSTANTS.dynamicBackground.intrusions.clickSuccessNarrative.replace('{detail}', activeIntrusion.narrativeDetail);
    integrateNarrative(narrative, `click_${activeIntrusion.id}`, { once: true });
    bumpRealityDrift(2);

    setActiveIntrusion(null);
    intrusionTimerRef.current = null;
  }, [activeIntrusion, bumpRealityDrift, integrateNarrative]);

  const startNewGame = useCallback(() => {
    setDiscovered(CONSTANTS.initialElements.slice());
    setHistory([]);
    setChapter(0);
    setHolyShitTriggered(false);
    setPlacedElements([]);
    setLightLevel(1);
    setReflectionActive(false);
    setGuestActive(false);
    setShadowCompanionActive(false);
    setDoorLocation('center');
    setBackgrounds({ current: ASSETS.backgrounds.houseRoom.path, previous: null });
    setRealityDrift(0);
    setElementArt({});
    setNarrative([CONSTANTS.narrative.initial]);
    setPlayerName('');
    setNameAsked(true);
    stopHeartbeat();
    setBackgroundEntities([]);
    setTemporaryEffects([]);
    setNarrativeEffects([]);
    setIsDoorAjar(false);
    setUsedNarratives({});
    setCraftingSlots([null, null]);
    setGameState('intro');
  }, [
    setChapter,
    setDiscovered,
    setHistory,
    setHolyShitTriggered,
    stopHeartbeat,
    setRealityDrift,
    setElementArt,
    setNarrative,
    setPlayerName,
    setBackgroundEntities,
    setBackgrounds,
    setIsDoorAjar,
    setUsedNarratives,
    setPlacedElements,
  ]);

  const continueGame = useCallback(() => {
    setGameState('playing');
    if (!ambientOn) {
      playAmbient();
    }
  }, [ambientOn, playAmbient]);

  const returnToMainMenu = useCallback(() => {
    setGameState('mainMenu');
    stopAmbient();
    setShowSettings(false);
    setTransitionTo(null);
  }, [stopAmbient, setTransitionTo]);

  const beginGame = useCallback(() => {
    const trimmed = nameInput.trim().slice(0, 40);
    const finalName = trimmed || 'Guest';
    setPlayerName(finalName);
    setNameAsked(false);
    playAmbient();
    setLightLevel(0.9);
    setDoorLocation((['left', 'center', 'right'] as DoorLocation[])[Math.floor(Math.random() * 3)]);
  }, [nameInput, playAmbient, setPlayerName]);

  const handleIntroComplete = useCallback(() => {
    setGameState('playing');
    setNameAsked(true);
    // Set initial background to match intro room
    setBackgrounds({ current: ASSETS.backgrounds.introRoom.path, previous: null });
    // Set initial elements from intro
    setDiscovered(CONSTANTS.intro.endingElements.slice());
  }, [setDiscovered, setBackgrounds]);

  const allowDrop = (e: React.DragEvent) => e.preventDefault();

  const doorPositionStyle: React.CSSProperties = useMemo(() => {
    let justify: string = 'center';
    if (doorLocation === 'left') justify = 'flex-start';
    if (doorLocation === 'right') justify = 'flex-end';
    return { justifyContent: justify };
  }, [doorLocation]);

  const ambientOverlayStyle: React.CSSProperties = useMemo(() => {
    if (isTutorial) {
      return {
        background: 'radial-gradient(circle, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)',
        opacity: 0.15, // Significantly reduced opacity for brightness
        pointerEvents: 'none',
      };
    }
    const darkness = Math.max(0, 1.2 - lightLevel);
    const baseOpacity = Math.min(0.45, darkness);
    const driftBonus = Math.min(0.2, (realityDrift / 100) * 0.18);
    return {
      background:
        'radial-gradient(60% 60% at 50% 50%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 70%, rgba(0,0,0,0.9) 100%)',
      opacity: baseOpacity + driftBonus,
      pointerEvents: 'none',
      transition: 'opacity 400ms ease',
    };
  }, [lightLevel, realityDrift, isTutorial]);

  // Calculate combined narrative effects style
  const narrativeEffectsStyle: React.CSSProperties = useMemo(() => {
    let combinedFilter = '';
    let combinedTransform = '';
    let overlayColor = '';
    let overlayOpacity = 0;

    narrativeEffects.forEach(effect => {
      const effectData = (CONSTANTS.narrativeEffects as any)[effect.id];
      if (!effectData) return;

      if (effect.type === 'filter' && effectData.filter) {
        combinedFilter += (combinedFilter ? ' ' : '') + effectData.filter;
      } else if (effect.type === 'transform' && effectData.transform) {
        combinedTransform += (combinedTransform ? ' ' : '') + effectData.transform;
      } else if (effect.type === 'overlay' && effectData.color && effectData.opacity) {
        overlayColor = effectData.color;
        overlayOpacity = Math.max(overlayOpacity, effectData.opacity);
      }
    });

    return {
      filter: combinedFilter || 'none',
      transform: combinedTransform || 'none',
      transition: 'filter 200ms ease, transform 100ms ease',
    };
  }, [narrativeEffects]);

  useEffect(() => {
    if (gameState !== 'playing' || isTutorial) return;
    if (realityDrift >= 60) {
      playHeartbeat();
      setReflectionActive(true);
    } else if (realityDrift < 45) {
      stopHeartbeat();
    }
  }, [realityDrift, playHeartbeat, stopHeartbeat, gameState, isTutorial]);

  // Inactivity timer
  useEffect(() => {
    if (gameState !== 'playing' || isTutorial) return;

    const interval = setInterval(() => {
      if (Date.now() - lastInteractionTime.current > CONSTANTS.realityDrift.inactivityInterval) {
        bumpRealityDrift(CONSTANTS.realityDrift.inactivityGain);
        getSfx('inactivityPulse').play();
        lastInteractionTime.current = Date.now(); // Reset after firing to avoid rapid succession
      }
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, [gameState, bumpRealityDrift, getSfx, isTutorial]);

  const handleUserInteraction = useCallback(() => {
    lastInteractionTime.current = Date.now();
  }, []);

  // Reality Drift effects - MERGED SYSTEM
  useEffect(() => {
    if (gameState !== 'playing' || isTutorial) return;

    // Audio Crossover
    const masterVol = volumeSettings.muted ? 0 : volumeSettings.master;
    const musicVol = masterVol * volumeSettings.music;
    if (realityDrift >= 75) {
      ambientRef.current?.fade(musicVol, 0, 1000);
      ambientTenseRef.current?.fade(0, musicVol, 1000);
    } else {
      ambientRef.current?.fade(0, musicVol, 1000);
      ambientTenseRef.current?.fade(musicVol, 0, 1000);
    }

    // Element position shifting - Keep PROJECT A's sophisticated system
    let elementShiftInterval: NodeJS.Timeout | null = null;
    if (realityDrift >= CONSTANTS.realityDrift.elementMovement.activationThreshold) {
      elementShiftInterval = setInterval(() => {
        setPlacedElements(prev => {
          const now = Date.now();
          const movableElements = prev.filter(el => (!el.anchoredUntil || el.anchoredUntil < now) && !el.isAboutToMove);
          if (movableElements.length === 0) return prev;

          const elementToMove = randomChoice(movableElements);

          // Set pre-move glow
          const withGlow = prev.map(el => el.id === elementToMove.id ? { ...el, isAboutToMove: true } : el);
          
          setTimeout(() => {
            setPlacedElements(currentPlaced => {
              const currentElementState = currentPlaced.find(p => p.id === elementToMove.id);
              // If element was removed in the meantime, do nothing
              if (!currentElementState) return currentPlaced;

              const moveAngle = Math.random() * 2 * Math.PI;
              const moveDistance = Math.random() * CONSTANTS.realityDrift.elementMovement.moveDistance;
              const newRawX = currentElementState.x + Math.cos(moveAngle) * moveDistance;
              const newRawY = currentElementState.y + Math.sin(moveAngle) * moveDistance;

              const constrainedPos = constrainPosition(newRawX, newRawY, PLACED_ELEMENT_SIZE, PLACED_ELEMENT_SIZE);

              return currentPlaced.map(el => 
                el.id === elementToMove.id 
                  ? { ...el, ...constrainedPos, isAboutToMove: false } 
                  : el
              );
            });
          }, CONSTANTS.realityDrift.elementMovement.preMoveGlowDuration);

          return withGlow;
        });
      }, getRandomInt(CONSTANTS.realityDrift.elementMovement.intervalMin, CONSTANTS.realityDrift.elementMovement.intervalMax));
    }

    // Background switching - Add PROJECT B's system
    let bgSwitchInterval: NodeJS.Timeout | null = null;
    if (realityDrift >= 75) {
      bgSwitchInterval = setInterval(() => {
        const unsettlingBgs = CONSTANTS.realityDrift.unsettlingBackgrounds;
        const newBgKey = randomChoice(unsettlingBgs);
        const newBgPath = (ASSETS.backgrounds as any)[newBgKey]?.path;
        if (newBgPath) {
          handleFullEnvironmentChange(newBgPath);
        }
      }, 15000);
    }

    return () => {
      if (elementShiftInterval) clearInterval(elementShiftInterval);
      if (bgSwitchInterval) clearInterval(bgSwitchInterval);
    };
  }, [realityDrift, gameState, volumeSettings, handleFullEnvironmentChange, setPlacedElements, constrainPosition, isTutorial]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (gameState === 'playing') {
          setGameState('paused');
        } else if (gameState === 'paused') {
          setGameState('playing');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState]);

  // End screen music handling
  useEffect(() => {
    if (gameState === 'gameEnd' || gameState === 'realityCollapse') {
      stopAmbient();
      stopHeartbeat();
      Object.values(sfxRef.current).forEach(sfx => {
        if (sfx.playing()) sfx.stop();
      });

      if (!endThemeRef.current) {
        endThemeRef.current = new Howl({
          src: [ASSETS.music.endTheme.path],
          loop: true,
        });
      }
      
      applyVolumeToAudio();

      if (!endThemeRef.current.playing()) {
        endThemeRef.current.play();
      }
    } else {
      if (endThemeRef.current?.playing()) {
        endThemeRef.current.stop();
      }
    }
  }, [gameState, stopAmbient, stopHeartbeat, applyVolumeToAudio]);

  useEffect(() => {
    return () => {
      endThemeRef.current?.unload();
    }
  }, []);

  const dustParticles = useMemo(() => Array.from({ length: isMobile ? 20 : 50 }).map((_, i) => ({
    id: i,
    duration: getRandomInt(20, 50),
    delay: getRandomInt(0, 50),
    size: getRandomInt(1, 2),
    startX: Math.random() * 100,
    xDrift: getRandomInt(-10, 10),
  })), [isMobile]);

  const ElementArt = ({ name, size = 20, drift = 0 }: { name: string; size?: number, drift?: number }) => {
    const artUrl = elementArt[name];

    const iconKey = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    const hasRealIcon = ASSETS.icons && (ASSETS.icons as any)[iconKey];

    useEffect(() => {
      if (hasRealIcon || artUrl) return;

      const generatorDiv = document.createElement('div');
      generatorDiv.style.position = 'fixed';
      generatorDiv.style.left = '-9999px';
      generatorDiv.style.width = '64px';
      generatorDiv.style.height = '64px';
      generatorDiv.style.display = 'flex';
      generatorDiv.style.alignItems = 'center';
      generatorDiv.style.justifyContent = 'center';
      generatorDiv.style.overflow = 'hidden';
      generatorDiv.style.background = '#000000';

      const rng = makeRng(simpleHash(name) + drift);
      const imagePromises: Promise<void>[] = [];

      // Base background texture
      const bgTextureKey = rng.pick(CONSTANTS.proceduralGen.backgroundTextures);
      const bgTexture = (ASSETS.procedural as any)[`texture_${bgTextureKey}`];
      if (bgTexture) {
        const bgImg = document.createElement('img');
        bgImg.crossOrigin = 'anonymous';
        const promise = new Promise<void>((res, rej) => { bgImg.onload = () => res(); bgImg.onerror = rej; });
        imagePromises.push(promise);
        bgImg.src = bgTexture.path;

        const bgHue = rng.next() * 360;
        bgImg.style.position = 'absolute';
        bgImg.style.inset = '0';
        bgImg.style.width = '100%';
        bgImg.style.height = '100%';
        bgImg.style.backgroundSize = 'cover';
        bgImg.style.filter = `hue-rotate(${bgHue}deg) brightness(0.4) saturate(0.7) contrast(1.5)`;
        bgImg.style.opacity = '0.6';
        generatorDiv.appendChild(bgImg);
      }

      // Add layers
      const numLayers = Math.floor(rng.next() * 3) + 2; // 2 to 4 layers
      const allSymbolKeys = CONSTANTS.proceduralGen.symbols;
      const allOverlayKeys = CONSTANTS.proceduralGen.overlays;

      for (let i = 0; i < numLayers; i++) {
        const isSymbol = rng.next() > 0.3;
        const assetKeyPool = isSymbol ? allSymbolKeys : allOverlayKeys;
        if (assetKeyPool.length === 0) continue;

        const assetKey = rng.pick(assetKeyPool);
        const asset = (ASSETS.procedural as any)[`${isSymbol ? 'symbol' : 'overlay'}_${assetKey}`];
        
        if (!asset) continue;

        const img = document.createElement('img');
        img.crossOrigin = 'anonymous';
        const promise = new Promise<void>((res, rej) => { img.onload = () => res(); img.onerror = rej; });
        imagePromises.push(promise);
        img.src = asset.path;

        img.style.position = 'absolute';
        img.style.mixBlendMode = rng.pick(['overlay', 'screen', 'soft-light', 'difference']);
        
        const size = (rng.next() * 0.6 + 0.4) * 100;
        img.style.width = `${size}%`;
        img.style.height = `${size}%`;

        const hue = rng.next() * 360;
        const brightness = rng.next() * 0.5 + 0.8;
        const contrast = rng.next() * 0.8 + 0.7 + (drift / 100) * 0.5;
        const saturate = rng.next() * 1.5 + (drift / 100);
        const blurAmount = rng.next() > 0.8 ? rng.next() * 1.5 : 0;
        const blur = `${blurAmount + (drift / 100) * 2}px`;
        img.style.filter = `hue-rotate(${hue}deg) brightness(${brightness}) contrast(${contrast}) saturate(${saturate}) blur(${blur})`;

        const opacity = rng.next() * 0.5 + 0.3;
        img.style.opacity = String(opacity);

        const rotate = (rng.next() - 0.5) * 360;
        const scale = rng.next() * 0.5 + 0.8;
        const translateX = (rng.next() - 0.5) * 20;
        const translateY = (rng.next() - 0.5) * 20;
        img.style.transform = `translate(${translateX}px, ${translateY}px) rotate(${rotate}deg) scale(${scale})`;

        generatorDiv.appendChild(img);
      }

      document.body.appendChild(generatorDiv);

      Promise.all(imagePromises)
        .then(() => {
          html2canvas(generatorDiv, { backgroundColor: null, useCORS: true })
            .then((canvas) => {
              const dataUrl = canvas.toDataURL();
              setElementArt((prev) => ({ ...prev, [name]: dataUrl }));
            })
            .catch(e => console.error("html2canvas failed:", e))
            .finally(() => {
              if (document.body.contains(generatorDiv)) {
                document.body.removeChild(generatorDiv);
              }
            });
        })
        .catch(e => {
          console.error("Image loading for procedural icon failed:", e);
          if (document.body.contains(generatorDiv)) {
            document.body.removeChild(generatorDiv);
          }
        });
    }, [name, artUrl, hasRealIcon, setElementArt, drift]);

    if (hasRealIcon) {
      return (
        <img 
          src={(ASSETS.icons as any)[iconKey].path} 
          width={size} 
          height={size} 
          alt={name} 
          className="opacity-90 rounded-sm" 
          draggable={false}
        />
      );
    }

    if (artUrl) {
      return <img src={artUrl} width={size} height={size} alt={name} className="opacity-90 rounded-sm" draggable={false} />;
    }

    return <div style={{ width: size, height: size }} className="bg-[#1f2328] animate-pulse rounded-sm" />;
  };

  const PlacedElementComponent = ({ el }: { el: PlacedElement }) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const isVibrating = !isTutorial && realityDrift >= CONSTANTS.realityDrift.elementMovement.activationThreshold;
    const isAboutToMove = !isTutorial && !!el.isAboutToMove;
  
    return (
      <motion.div
        ref={elementRef}
        key={el.id}
        drag={!isMobile}
        dragMomentum={false}
        onDragStart={!isMobile ? () => { setTooltip(null); handleUserInteraction(); } : undefined}
        onDragEnd={!isMobile ? (event, info) => { handleElementDragEnd(el.id, info, elementRef.current); handleUserInteraction(); } : undefined}
        onClick={isMobile ? (e) => {
            setMobileContextMenu({ name: el.name, id: el.id, x: e.clientX, y: e.clientY, target: 'scene' });
        } : undefined}
        whileDrag={{ scale: 1.1, zIndex: 10 }}
        className="absolute cursor-grab active:cursor-grabbing"
        style={{
          left: el.x,
          top: el.y,
        }}
        animate={{
          // Corrected relative vibration
          x: isVibrating ? [0, 1, -1, -1, 1, 0] : 0,
          y: isVibrating ? [0, -1, 0, 1, -1, 0] : 0,
          // New pre-move glow effect
          scale: isAboutToMove ? 1.2 : 1.0,
          filter: isAboutToMove ? 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.7))' : 'none',
        }}
        transition={{
          x: isVibrating ? { duration: 0.4, repeat: Infinity, ease: 'linear' } : { duration: 0.2 },
          y: isVibrating ? { duration: 0.4, repeat: Infinity, ease: 'linear' } : { duration: 0.2 },
          scale: { duration: 0.5, ease: 'easeInOut' },
          filter: { duration: 0.5, ease: 'easeInOut' },
        }}
      >
        <ElementArt name={el.name} size={PLACED_ELEMENT_SIZE} drift={isTutorial ? 0 : realityDrift} />
      </motion.div>
    );
  };

  const Slider = ({ label, value, onChange, min, max, step, displayMultiplier = 100, displaySuffix = '%' }: { 
    label: string; 
    value: number; 
    onChange: (value: number) => void;
    min: number;
    max: number;
    step: number;
    displayMultiplier?: number;
    displaySuffix?: string;
  }) => (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span>{Math.round(value * displayMultiplier)}{displaySuffix}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-[#1f2328] rounded-lg appearance-none cursor-pointer slider"
      />
    </div>
  );

  const SettingsPanel = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium mb-4">Settings</h3>

      <Slider
        label="Brightness"
        value={volumeSettings.brightness}
        onChange={(value) => setVolumeSettings((prev) => ({ ...prev, brightness: value }))}
        min={0.5}
        max={1.5}
        step={0.05}
      />

      <Slider
        label="Master Volume"
        value={volumeSettings.master}
        onChange={(value) => setVolumeSettings((prev) => ({ ...prev, master: value }))}
        min={0}
        max={1}
        step={0.1}
      />

      <Slider
        label="Music Volume"
        value={volumeSettings.music}
        onChange={(value) => setVolumeSettings((prev) => ({ ...prev, music: value }))}
        min={0}
        max={1}
        step={0.1}
      />

      <Slider
        label="SFX Volume"
        value={volumeSettings.sfx}
        onChange={(value) => setVolumeSettings((prev) => ({ ...prev, sfx: value }))}
        min={0}
        max={1}
        step={0.1}
      />

      <div className="flex items-center justify-between">
        <span className="text-sm">Mute All</span>
        <button
          onClick={() => setVolumeSettings((prev) => ({ ...prev, muted: !prev.muted }))}
          className={`px-3 py-1 text-xs rounded border ${
            volumeSettings.muted
              ? 'bg-[#ef4444] border-[#ef4444] text-white'
              : 'bg-[#1f2328] border-[#2f3540] hover:bg-[#2a2f36]'
          }`}
        >
          {volumeSettings.muted ? 'Unmute' : 'Mute'}
        </button>
      </div>
    </div>
  );

  const PARAGRAPHS_PER_PAGE = 3;
  const currentNarrative = isTutorial ? tutorialNarrative : narrative;
  const totalPages = Math.ceil(currentNarrative.length / PARAGRAPHS_PER_PAGE);
  const narrativePage = currentNarrative.slice(
    historyPage * PARAGRAPHS_PER_PAGE,
    (historyPage + 1) * PARAGRAPHS_PER_PAGE
  );
  const prevPage = () => setHistoryPage((p) => Math.max(0, p - 1));
  const nextPage = () => setHistoryPage((p) => Math.min(totalPages - 1, p + 1));

  const handleMobileElementTap = (elementName: string) => {
    handleUserInteraction();
    setCraftingSlots(prev => {
        const newSlots = [...prev];
        const emptySlotIndex = newSlots.indexOf(null);
        if (emptySlotIndex !== -1) {
            newSlots[emptySlotIndex] = elementName;
        }
        return newSlots;
    });
  };

  const handleMobileCombine = () => {
    handleUserInteraction();
    if (craftingSlots[0] && craftingSlots[1]) {
        onCombine(craftingSlots[0], craftingSlots[1]);
        setCraftingSlots([null, null]);
    }
  };

  const handleTouchStart = (e: React.TouchEvent, elementName: string) => {
    handleUserInteraction();
    isLongPress.current = false;
    if (longPressTimeoutRef.current) clearTimeout(longPressTimeoutRef.current);
    touchStartPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };

    longPressTimeoutRef.current = setTimeout(() => {
        isLongPress.current = true;
        setMobileContextMenu({
            name: elementName,
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
            target: 'inventory',
        });
    }, CONSTANTS.ui.longPressDuration);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartPos.current || !longPressTimeoutRef.current) return;
    const dx = Math.abs(e.touches[0].clientX - touchStartPos.current.x);
    const dy = Math.abs(e.touches[0].clientY - touchStartPos.current.y);
    if (dx > 10 || dy > 10) {
        clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
        touchStartPos.current = null;
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
        longPressTimeoutRef.current = null;
    }
    touchStartPos.current = null;
  };

  const handleMobileClick = (elementName: string) => {
    if (isLongPress.current) return;
    handleMobileElementTap(elementName);
  };

  const handlePlaceElement = (name: string) => {
    if (!sceneRef.current) return;
    const rect = sceneRef.current.getBoundingClientRect();
    const x = Math.random() * (rect.width - PLACED_ELEMENT_SIZE);
    const y = Math.random() * (rect.height - PLACED_ELEMENT_SIZE);

    const newElement: PlacedElement = {
        id: nextId.current++,
        name,
        x,
        y,
    };
    const stateSetter = isTutorial ? setTutorialPlaced : setPlacedElements;
    stateSetter(prev => [...prev, newElement]);
    setMobileContextMenu(null);
  };

  const MobileContextMenu = () => {
    if (!mobileContextMenu) return null;

    const { name, x, y, target, id } = mobileContextMenu;

    const menuStyle: React.CSSProperties = {
        position: 'fixed',
        left: x,
        top: y,
        transform: 'translate(-50%, -100%)',
        // Adjust position to stay within viewport
        ...(y < 150 && { transform: 'translate(-50%, 20px)' }),
        ...(x < 100 && { left: 100 }),
        ...(x > window.innerWidth - 100 && { left: window.innerWidth - 100 }),
    };

    return (
        <motion.div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setMobileContextMenu(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <motion.div
                style={menuStyle}
                className="absolute w-48 rounded-md border border-[#2f3540] bg-[#0c0e11] p-2 shadow-lg z-50"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="text-center text-sm font-semibold mb-2 pb-2 border-b border-[#222529]">{titleCaseName(name)}</div>
                <div className="flex flex-col gap-1">
                    {target === 'inventory' && (
                        <>
                            <button onClick={() => { handlePlaceElement(name); }} className="context-menu-button">Place in Scene</button>
                            <button onClick={() => { handleMobileElementTap(name); setMobileContextMenu(null); }} className="context-menu-button">Add to Crafting Slot</button>
                        </>
                    )}
                    {target === 'scene' && (
                        <>
                            <button onClick={() => { handleMobileElementTap(name); setPlacedElements(p => p.filter(el => el.id !== id)); setMobileContextMenu(null); }} className="context-menu-button">Use for Combination</button>
                            <button onClick={() => { setPlacedElements(p => p.filter(el => el.id !== id)); setMobileContextMenu(null); }} className="context-menu-button">Return to Discoveries</button>
                        </>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
  };

  if (gameState === 'intro') {
    return (
      <IntroSequence
        getSfx={getSfx}
        playWhisper={playWhisper}
        volumeSettings={volumeSettings}
        onComplete={handleIntroComplete}
        isMobile={isMobile}
      />
    );
  }

  if (gameState === 'mainMenu') {
    return (
      <div
        className="min-h-screen w-full text-[#e5e7eb] flex items-center justify-center relative overflow-hidden"
        style={{ filter: `brightness(${volumeSettings.brightness})` }}
      >
        <MainMenuBackground
          getSfx={getSfx}
          playWhisper={playWhisper}
          volumeSettings={volumeSettings}
          isTransitioning={!!transitionTo}
          isMobile={isMobile}
        />
        <AnimatePresence>
          {!transitionTo && (
            <motion.div
              exit={{ opacity: 0, filter: 'blur(5px)' }}
              transition={{ duration: CONSTANTS.doorTransition.fadeOutDuration / 1000 }}
              className="w-full max-w-md p-6 relative"
            >
              <div className="flex flex-col items-center space-y-6">
                <AnimatedLogo 
                  width={isMobile ? 300 : 600} 
                  onVariantChange={(variant) => setLastLogoVariant(variant)}
                />
                <div className="text-center">
                  <p className="text-sm text-[#9ca3af] mt-1">{CONSTANTS.menu.subtitle}</p>
                </div>
              </div>

              <div className="mt-8">
                {showSettings ? (
                  <div className="rounded-md border border-[#222529] bg-[#0c0e11]/80 backdrop-blur-sm p-4">
                    <SettingsPanel />
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={() => setShowSettings(false)}
                        className="px-4 py-2 text-sm rounded bg-[#1f2328] hover:bg-[#2a2f36] border border-[#2f3540]"
                      >
                        Back
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <button
                      onClick={() => setTransitionTo('intro')}
                      className={`w-full px-4 py-3 text-sm rounded bg-[#1f2328]/80 backdrop-blur-sm hover:bg-[#2a2f36]/80 border border-[#2f3540] transition-all duration-300 ${highlightNewGame ? 'new-game-highlight' : ''}`}
                    >
                      {CONSTANTS.menu.newGame}
                    </button>

                    {hasSavedGame && (
                      <button
                        onClick={() => setTransitionTo('playing')}
                        className="w-full px-4 py-3 text-sm rounded bg-[#1f2328]/80 backdrop-blur-sm hover:bg-[#2a2f36]/80 border border-[#2f3540] transition-colors"
                      >
                        {CONSTANTS.menu.continue}
                      </button>
                    )}

                    <button
                      onClick={() => {
                        setTutorialStep(0);
                        setTutorialDiscovered(CONSTANTS.tutorial.initialElements);
                        setTutorialNarrative([CONSTANTS.tutorial.initialNarrative]);
                        setTutorialPlaced([]);
                        setTutorialActionCompleted(false);
                        setGameState('tutorial');
                      }}
                      className="w-full px-4 py-3 text-sm rounded bg-[#1f2328]/80 backdrop-blur-sm hover:bg-[#2a2f36]/80 border border-[#2f3540] transition-colors"
                    >
                      How to Play
                    </button>

                    <button
                      onClick={() => setShowSettings(true)}
                      className="w-full px-4 py-3 text-sm rounded bg-[#1f2328]/80 backdrop-blur-sm hover:bg-[#2a2f36]/80 border border-[#2f3540] transition-colors"
                    >
                      {CONSTANTS.menu.settings}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        <AnimatePresence>
          {transitionTo && (
            <DoorTransition
              getSfx={getSfx}
              playWhisper={playWhisper}
              volumeSettings={volumeSettings}
              onComplete={() => {
                if (transitionTo === 'intro') {
                  startNewGame();
                } else if (transitionTo === 'playing') {
                  continueGame();
                }
              }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (gameState === 'realityCollapse') {
    return (
      <motion.div 
        className="fixed inset-0 bg-black flex items-center justify-center text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2 }}
        style={{ filter: `brightness(${volumeSettings.brightness})` }}
      >
        <motion.img
          src={ASSETS.backgrounds.realityCollapse.path}
          className="absolute inset-0 w-full h-full object-cover"
          animate={{
            scale: [1, 1.2, 1.1, 1.3, 1],
            filter: ['blur(0px)', 'blur(5px)', 'blur(2px)', 'blur(8px)', 'blur(0px)'],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div 
          className="relative z-10 text-center p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 3, delay: 1 }}
        >
          <h2 className="text-4xl font-serif mb-4">The house has forgotten you.</h2>
          <p className="text-lg text-gray-300 mb-8">There is nothing left but the echo of what was.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowFullStory(true)}
              className="px-6 py-3 text-sm rounded bg-[#1f2328]/80 backdrop-blur-sm hover:bg-[#2a2f36]/80 border border-[#2f3540] transition-colors"
            >
              Review Your Descent
            </button>
            <button
              onClick={startNewGame}
              className="px-6 py-3 text-sm rounded bg-[#1f2328]/80 backdrop-blur-sm hover:bg-[#2a2f36]/80 border border-[#2f3540] transition-colors"
            >
              Start Anew
            </button>
          </div>
        </motion.div>
        <AnimatePresence>
          {showFullStory && (
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFullStory(false)}
            >
              <motion.div
                className="w-full max-w-2xl h-[80vh] rounded-md border border-[#2f3540] bg-[#0c0e11] flex flex-col"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4 border-b border-[#222529] flex items-center justify-between">
                  <h2 className="text-lg">Your Descent</h2>
                  <button onClick={() => setShowFullStory(false)} className="text-2xl text-[#9aa0a6] hover:text-white">
                    &times;
                  </button>
                </div>
                <div className="p-4 overflow-y-auto flex-grow">
                  <div className="whitespace-pre-wrap text-[#cbd5e1] text-sm leading-relaxed space-y-4">
                    {narrative.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  if (gameState === 'gameEnd') {
    return (
      <motion.div 
        className="fixed inset-0 bg-black flex items-center justify-center text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2 }}
        style={{ filter: `brightness(${volumeSettings.brightness})` }}
      >
        <motion.img
          src={ASSETS.backgrounds.houseRoom.path}
          className="absolute inset-0 w-full h-full object-cover"
          animate={{
            scale: 1.1,
            filter: ['brightness(0.7)', 'brightness(0.5)', 'brightness(0.7)'],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="absolute inset-0 bg-black/30" />
        <motion.div 
          className="relative z-10 text-center p-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 3, delay: 1 }}
        >
          <h2 className="text-4xl font-serif mb-4">You have found the truth.</h2>
          <p className="text-lg text-gray-300 mb-8">The house was a memory. You are free to leave.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowFullStory(true)}
              className="px-6 py-3 text-sm rounded bg-[#1f2328]/80 backdrop-blur-sm hover:bg-[#2a2f36]/80 border border-[#2f3540] transition-colors"
            >
              Read Full Story
            </button>
            <button
              onClick={startNewGame}
              className="px-6 py-3 text-sm rounded bg-[#1f2328]/80 backdrop-blur-sm hover:bg-[#2a2f36]/80 border border-[#2f3540] transition-colors"
            >
              Start Anew
            </button>
          </div>
        </motion.div>
        <AnimatePresence>
          {showFullStory && (
            <motion.div
              className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFullStory(false)}
            >
              <motion.div
                className="w-full max-w-2xl h-[80vh] rounded-md border border-[#2f3540] bg-[#0c0e11] flex flex-col"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4 border-b border-[#222529] flex items-center justify-between">
                  <h2 className="text-lg">The Full Story</h2>
                  <button onClick={() => setShowFullStory(false)} className="text-2xl text-[#9aa0a6] hover:text-white">
                    &times;
                  </button>
                </div>
                <div className="p-4 overflow-y-auto flex-grow">
                  <div className="whitespace-pre-wrap text-[#cbd5e1] text-sm leading-relaxed space-y-4">
                    {narrative.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  const gameOrTutorialRender = (
    <div
      className="min-h-screen w-full bg-[#0b0c0e] text-[#e5e7eb] flex flex-col"
      onClick={handleUserInteraction}
      onKeyDown={handleUserInteraction}
      style={{ filter: `brightness(${volumeSettings.brightness})` }}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-[#222529]">
        <div className="flex items-center gap-3">
          <AnimatedLogo width={isMobile ? 120 : 180} lockVariant={lastLogoVariant} />
        </div>
        <div className="flex items-center gap-3">
          <div className="leading-tight hidden sm:block">
            <div className="text-sm tracking-wide uppercase text-[#9ca3af]">Underneath the house</div>
            <div className="text-xs text-[#6b7280]">Chapter: {isTutorial ? 'Tutorial' : chapter}</div>
          </div>
          <div ref={driftMeterRef} className="hidden sm:flex flex-col items-end">
            <div className="text-[10px] text-[#9aa0a6]">Reality Drift {isTutorial ? 0 : realityDrift}%</div>
            <div className="w-[140px] h-[8px] rounded-sm bg-[#1a1d22] border border-[#2f3540] overflow-hidden">
              <div
                className="h-full transition-all"
                style={{
                  width: `${isTutorial ? 0 : realityDrift}%`,
                  background: realityDrift < 33 ? '#3b82f6' : realityDrift < 66 ? '#f59e0b' : '#ef4444',
                }}
              />
            </div>
          </div>
          <button
            onClick={() => setGameState('paused')}
            className="px-3 py-1.5 text-xs rounded bg-[#1f2328] hover:bg-[#2a2f36] border border-[#2f3540]"
            title="Pause (ESC)"
          >
            {CONSTANTS.menu.pause}
          </button>
        </div>
      </header>

      <main className="grid grid-cols-1 lg:grid-cols-3 gap-3 p-3 flex-grow">
        <section className="col-span-1 lg:col-span-2 relative rounded-md border border-[#222529] overflow-hidden">
          <div 
            ref={sceneRef}
            onDrop={isMobile ? undefined : handleDropOnScene}
            onDragOver={isMobile ? undefined : allowDrop}
            className="relative h-[55vh] lg:h-full bg-[#0f1114]"
            style={{
              ...narrativeEffectsStyle,
              filter: isTutorial ? 'none' : `saturate(${1 - realityDrift / 200}) blur(${realityDrift / 100}px) ${narrativeEffectsStyle.filter || ''}`,
              transition: 'filter 1s ease-in-out',
            }}
          >
            <AnimatePresence>
              <motion.img
                key={backgrounds.current}
                src={backgrounds.current}
                alt="House Room"
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ opacity: 0 }}
                animate={{ opacity: isTutorial ? 1 : 0.7 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5, ease: 'linear' }}
                style={{
                  filter: isTutorial ? 'brightness(1.1)' : (backgroundShift?.filter || 'none'),
                  transition: `filter ${CONSTANTS.dynamicBackground.subtleShifts.fadeDuration}ms ease-in-out`
                }}
              />
            </AnimatePresence>
            <div className="absolute inset-0" style={ambientOverlayStyle} />

            {/* Fixed History Button in Scene */}
            <button
              ref={historyButtonRef}
              onClick={() => {
                setHistoryPage(0);
                setShowHistory(true);
              }}
              className="absolute top-4 right-4 z-30 p-2 rounded bg-[#1f2328]/80 hover:bg-[#2a2f36]/80 border border-[#2f3540] backdrop-blur-sm pointer-events-auto"
              title="Show Narrative History"
              style={{ pointerEvents: 'auto' }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
            >
              <img src={ASSETS.ui.historyJournal.path} width={28} height={28} alt="History" className="opacity-80" />
            </button>

            {/* Dust Particles */}
            {dustParticles.map(p => (
              <motion.div
                key={p.id}
                className="absolute bg-white/10 rounded-full pointer-events-none"
                style={{
                  width: p.size,
                  height: p.size,
                  left: `${p.startX}vw`,
                  top: '-5%',
                }}
                animate={{
                  y: '110vh',
                  x: `${p.xDrift}vw`,
                }}
                transition={{
                  duration: p.duration,
                  delay: p.delay,
                  repeat: Infinity,
                  ease: 'linear',
                  repeatType: 'loop',
                }}
              />
            ))}

            {/* Narrative Effect Overlay */}
            <AnimatePresence>
              {narrativeEffects.map(effect => {
                const effectData = (CONSTANTS.narrativeEffects as any)[effect.id];
                if (!effectData) return null;
                return (
                  <motion.div
                    key={effect.key}
                    className="absolute inset-0 pointer-events-none"
                    style={{ backgroundColor: effectData.color }}
                    initial={{ opacity: 0 }}
                    animate={{ 
                      opacity: effectData.pulses 
                        ? Array(effectData.pulses).fill([0, effectData.opacity, 0]).flat()
                        : effectData.opacity 
                    }}
                    transition={{ 
                      duration: effectData.duration / 1000,
                      times: effectData.pulses 
                        ? Array(effectData.pulses).fill([0, 0.5, 1]).flat().map((v, i) => i / (effectData.pulses * 3 - 1))
                        : undefined
                    }}
                    exit={{ opacity: 0 }}
                  />
                );
              })}
            </AnimatePresence>

            {/* Permanent Background Entities */}
            {backgroundEntities.map(id => {
              const entityData = (CONSTANTS.backgroundEvents as any)[id];
              if (!entityData || !entityData.asset) return null;
              return (
                <motion.img
                  key={id}
                  src={(ASSETS.backgroundEntities as any)[entityData.asset].path}
                  alt={id}
                  className="absolute pointer-events-none opacity-80"
                  style={entityData.position}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.8 }}
                  transition={{ duration: 1.5 }}
                />
              );
            })}

            {/* Temporary Effects */}
            <AnimatePresence>
              {temporaryEffects.map(effect => {
                const effectData = (CONSTANTS.backgroundEvents as any)[effect.id];
                if (!effectData) return null;
                
                if (effectData.type === 'filter') {
                  // Filter effects are handled by the main scene container
                  return null;
                }
                
                if (!effectData.asset) return null;
                
                return (
                  <motion.div
                    key={effect.key}
                    className="absolute pointer-events-none"
                    style={effectData.position}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.7, 0.7, 0], transition: { duration: effect.duration / 1000, times: [0, 0.1, 0.9, 1] } }}
                    exit={{ opacity: 0 }}
                  >
                    <img src={(ASSETS.backgroundEntities as any)[effectData.asset].path} alt={effect.id} className="w-full h-full" />
                  </motion.div>
                );
              })}
            </AnimatePresence>

            <AnimatePresence>
              {activeIntrusion && (
                <motion.div
                  key={activeIntrusion.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 1, 1, 0], transition: { duration: CONSTANTS.dynamicBackground.intrusions.duration / 1000, times: [0, 0.1, 0.9, 1] } }}
                  exit={{ opacity: 0 }}
                  className="absolute z-10 cursor-pointer"
                  style={{ ...activeIntrusion.position, width: activeIntrusion.size.width, height: activeIntrusion.size.height }}
                  onClick={handleIntrusionClick}
                >
                  <img src={(ASSETS.intrusions as any)[activeIntrusion.asset].path} alt="An intrusion" className="w-full h-full object-contain opacity-80" />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {!isMobile && enlargedElementDisplay && (
                <motion.div
                  key={enlargedElementDisplay.id}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1.5, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0, transition: { duration: 0.3 } }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="absolute pointer-events-none z-20"
                  style={{
                    left: enlargedElementDisplay.x,
                    top: enlargedElementDisplay.y,
                    x: '-50%',
                    y: '-50%',
                  }}
                >
                  <ElementArt name={enlargedElementDisplay.name} size={CONSTANTS.ui.enlargedElementSize} drift={isTutorial ? 0 : realityDrift} />
                </motion.div>
              )}
            </AnimatePresence>

            {(isTutorial ? tutorialPlaced : placedElements).map((el) => (
              <PlacedElementComponent el={el} key={el.id} />
            ))}

            <div className="absolute bottom-6 left-0 right-0 flex" style={doorPositionStyle}>
              <motion.img
                key={doorLocation}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 0.95, y: 0 }}
                transition={{ duration: 0.6 }}
                src={isDoorAjar ? ASSETS.ui.doorAjar.path : (chapter >= 1 ? ASSETS.ui.mysteriousDoor.path : ASSETS.ui.door.path)}
                width={72}
                height={120}
                alt="Door"
                className="opacity-80"
              />
            </div>

            <AnimatePresence>
              {reflectionActive && (
                <motion.img
                  key="reflection"
                  initial={{ opacity: 0, x: -40 }}
                  animate={{
                    opacity: [0.2, 0.6, 0.3, 0.7],
                    x: [0, 20, -10, 30, -20, 40],
                  }}
                  transition={{ duration: 6, repeat: Infinity, repeatType: 'mirror' }}
                  src={ASSETS.entities.reflection.path}
                  width={120}
                  height={200}
                  alt="Reflection"
                  className="absolute bottom-10 left-8 opacity-70"
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {guestActive && (
                <motion.img
                  key="guest"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{
                    opacity: [0.3, 0.8, 0.4, 0.9],
                    scale: [0.8, 1.1, 0.9, 1.2],
                    x: [0, -10, 5, -15],
                  }}
                  transition={{ duration: 8, repeat: Infinity, repeatType: 'mirror' }}
                  src={ASSETS.entities.theGuest.path}
                  width={120}
                  height={200}
                  alt="The Guest"
                  className="absolute bottom-10 right-8 opacity-60"
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {shadowCompanionActive && (
                <motion.div
                  key="shadowCompanion"
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: [0.1, 0.4, 0.2, 0.5],
                    x: [0, 5, -3, 8],
                  }}
                  transition={{ duration: 10, repeat: Infinity, repeatType: 'mirror' }}
                  className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
                >
                  <img
                    src={ASSETS.entities.shadowCompanion.path}
                    width={100}
                    height={180}
                    alt="Shadow Companion"
                    className="opacity-30"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {logVisible && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.3 }}
                  className="absolute bottom-4 left-4 right-4 sm:right-auto sm:max-w-md p-3 space-y-1 rounded-md bg-black/60 backdrop-blur-sm border border-white/10 text-xs text-[#cbd5e1] pointer-events-none"
                >
                  {gameLog.map((msg, i) => (
                    <p key={i} className={i > 0 ? 'opacity-60' : ''}>
                      {msg}
                    </p>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        <section className="col-span-1 space-y-3 flex flex-col">
          <div className="rounded-md border border-[#222529] bg-[#0c0e11] p-2">
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); handleUserInteraction(); }}
              placeholder="Search discoveries…"
              className="w-full bg-transparent outline-none text-sm px-2 py-1"
            />
          </div>

          {isMobile && (
            <div ref={craftingSlotsRef} className="rounded-md border border-[#222529] bg-[#0c0e11] p-2 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                    {craftingSlots.map((slot, index) => (
                        <div key={index} className={`crafting-slot ${slot ? 'crafting-slot-filled' : ''}`} onClick={() => {
                            if (slot) {
                                setCraftingSlots(prev => {
                                    const newSlots = [...prev];
                                    newSlots[index] = null;
                                    return newSlots;
                                });
                            }
                        }}>
                            {slot ? <ElementArt name={slot} size={32} drift={isTutorial ? 0 : realityDrift} /> : <span className="text-gray-500 italic">{index === 0 ? 'First Component' : 'Second Component'}</span>}
                        </div>
                    ))}
                </div>
                <button 
                    onClick={handleMobileCombine}
                    disabled={!craftingSlots[0] || !craftingSlots[1]}
                    className="w-full px-4 py-2 text-sm rounded bg-[#1f2328] hover:bg-[#2a2f36] border border-[#2f3540] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Combine
                </button>
            </div>
          )}

          <div ref={discoveriesPanelRef} className="rounded-md border border-[#222529] bg-[#0c0e11] p-2 flex-grow overflow-y-auto">
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-4 gap-2">
              {filteredElements.map((el) => (
                <button
                  key={el}
                  draggable={!isMobile}
                  onDragStart={isMobile ? undefined : (e) => {
                    setTooltip(null);
                    e.dataTransfer.setData('text/plain', el);
                    e.dataTransfer.setData('application/x-udch-element', 'true');
                    handleUserInteraction();
                  }}
                  onMouseEnter={isMobile ? undefined : (e) => setTooltip({ name: titleCaseName(el), x: e.clientX + 15, y: e.clientY + 15 })}
                  onMouseLeave={isMobile ? undefined : () => setTooltip(null)}
                  onMouseMove={isMobile ? undefined : (e) => setTooltip(prev => prev ? { ...prev, x: e.clientX + 15, y: e.clientY + 15 } : null)}
                  onClick={isMobile ? () => handleMobileClick(el) : undefined}
                  onTouchStart={isMobile ? (e) => handleTouchStart(e, el) : undefined}
                  onTouchEnd={isMobile ? handleTouchEnd : undefined}
                  onTouchMove={isMobile ? (e) => handleTouchMove(e) : undefined}
                  className="flex items-center justify-center rounded border border-[#2f3540] bg-[#101318] hover:bg-[#141821] active:scale-[0.98] transition p-2 aspect-square"
                >
                  <ElementArt name={el} size={24} drift={isTutorial ? 0 : realityDrift} />
                </button>
              ))}
            </div>
            <div className="mt-2 text-[10px] text-[#6b7280]">Discovered: {filteredElements.length}</div>
          </div>
        </section>
      </main>

      <AnimatePresence>
        {tooltip && !isMobile && <Tooltip content={tooltip.name} x={tooltip.x} y={tooltip.y} />}
      </AnimatePresence>

      <AnimatePresence>
        {isMobile && mobileContextMenu && <MobileContextMenu />}
      </AnimatePresence>

      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            className="fixed inset-0 bg-transparent z-50 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {activeJumpscare && (
              <img src={(ASSETS.jumpscares as any)[activeJumpscare].path} alt="jumpscare" className="w-full h-full object-contain" />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {nameAsked && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-[92%] max-w-[420px] rounded-md border border-[#2f3540] bg-[#0c0e11] p-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
            >
              <div className="text-lg mb-2">What is your name?</div>
              <div className="text-sm text-[#9aa0a6] mb-3">
                It helps to remember who you are. Before all this.
              </div>
              <input
                autoFocus
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="Enter your name..."
                className="w-full bg-[#0f1216] border border-[#2f3540] rounded px-3 py-2 text-sm outline-none"
              />
              <div className="mt-3 flex items-center justify-between">
                <button
                  onClick={beginGame}
                  className="px-3 py-1.5 text-sm rounded bg-[#1f2328] hover:bg-[#2a2f36] border border-[#2f3540]"
                >
                  Continue
                </button>
                <button
                  onClick={() => {
                    setPlayerName('Guest');
                    setNameAsked(false);
                    playAmbient();
                  }}
                  className="text-xs text-[#9aa0a6] hover:text-[#e5e7eb]"
                >
                  Skip
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {gameState === 'paused' && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-[92%] max-w-[420px] rounded-md border border-[#2f3540] bg-[#0c0e11] p-4"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
            >
              <div className="text-lg mb-4">Game Paused</div>

              {showSettings ? (
                <>
                  <SettingsPanel />
                  <div className="mt-6 flex gap-2">
                    <button
                      onClick={() => setShowSettings(false)}
                      className="flex-1 px-4 py-2 text-sm rounded bg-[#1f2328] hover:bg-[#2a2f36] border border-[#2f3540]"
                    >
                      Back
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={() => setGameState('playing')}
                    className="w-full px-4 py-2 text-sm rounded bg-[#1f2328] hover:bg-[#2a2f36] border border-[#2f3540]"
                  >
                    {CONSTANTS.menu.resume}
                  </button>

                  <button
                    onClick={() => setShowSettings(true)}
                    className="w-full px-4 py-2 text-sm rounded bg-[#1f2328] hover:bg-[#2a2f36] border border-[#2f3540]"
                  >
                    {CONSTANTS.menu.settings}
                  </button>

                  <button
                    onClick={returnToMainMenu}
                    className="w-full px-4 py-2 text-sm rounded bg-[#1f2328] hover:bg-[#2a2f36] border border-[#2f3540]"
                  >
                    {CONSTANTS.menu.mainMenu}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showHistory && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowHistory(false)}
          >
            <motion.div
              className="w-full max-w-2xl h-[80vh] rounded-md border border-[#2f3540] bg-[#0c0e11] flex flex-col"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-[#222529] flex items-center justify-between">
                <h2 className="text-lg">Narrative Story</h2>
                <button onClick={() => setShowHistory(false)} className="text-2xl text-[#9aa0a6] hover:text-white">
                  &times;
                </button>
              </div>
              <div className="p-4 overflow-y-auto flex-grow">
                <div className="whitespace-pre-wrap text-[#cbd5e1] text-sm leading-relaxed space-y-4">
                  {narrativePage.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>
              <div className="p-3 border-t border-[#222529] flex items-center justify-between">
                <button
                  onClick={prevPage}
                  disabled={historyPage === 0}
                  className="p-2 rounded-full bg-[#1f2328] hover:bg-[#2a2f36] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <img src={ASSETS.ui.arrowLeft.path} width={16} height={16} alt="Previous" className="opacity-80" />
                </button>
                <span className="text-xs text-[#6b7280]">
                  Page {historyPage + 1} of {totalPages}
                </span>
                <button
                  onClick={nextPage}
                  disabled={historyPage >= totalPages - 1}
                  className="p-2 rounded-full bg-[#1f2328] hover:bg-[#2a2f36] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <img src={ASSETS.ui.arrowRight.path} width={16} height={16} alt="Next" className="opacity-80" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="px-4 py-3 border-t border-[#222529] text-[11px] text-[#888]">
        {isMobile ? CONSTANTS.ui.hintMobile : "Tip: Drag elements from your discoveries into the room to interact with them. Some discoveries will change the house."}
      </footer>
    </div>
  );

  if (gameState === 'playing' || gameState === 'tutorial') {
    return (
      <>
        {gameOrTutorialRender}
        {isTutorial && (
          <TutorialOverlay
            step={tutorialStep}
            setStep={setTutorialStep}
            isMobile={isMobile}
            onExit={() => {
              setGameState('mainMenu');
              setHighlightNewGame(true);
              setTimeout(() => setHighlightNewGame(false), 5000);
            }}
            actionCompleted={tutorialActionCompleted}
            setActionCompleted={setTutorialActionCompleted}
            refs={{
              discoveriesPanel: discoveriesPanelRef,
              sceneArea: sceneRef,
              craftingSlots: craftingSlotsRef,
              historyButton: historyButtonRef,
              driftMeter: driftMeterRef,
            }}
          />
        )}
      </>
    );
  }

  return gameOrTutorialRender;
}

const HighlightBox = ({ targetRef }: { targetRef: React.RefObject<HTMLElement> }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const updateRect = () => {
      if (targetRef.current) {
        setRect(targetRef.current.getBoundingClientRect());
      }
    };

    // Initial calculation
    updateRect();

    // Recalculate on resize
    window.addEventListener('resize', updateRect);
    
    // Also use a MutationObserver to catch layout shifts
    const observer = new MutationObserver(updateRect);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener('resize', updateRect);
      observer.disconnect();
    };
  }, [targetRef]);

  if (!rect || rect.width === 0) return null;

  return (
    <motion.div
      className="tutorial-highlight"
      style={{
        left: rect.left - 8,
        top: rect.top - 8,
        width: rect.width + 16,
        height: rect.height + 16,
      }}
      initial={{ opacity: 0, scale: 1.1 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    />
  );
};

const TutorialOverlay = ({
  step,
  setStep,
  isMobile,
  onExit,
  actionCompleted,
  setActionCompleted,
  refs,
}: {
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  isMobile: boolean;
  onExit: () => void;
  actionCompleted: boolean;
  setActionCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  refs: Record<string, React.RefObject<HTMLElement>>;
}) => {
  const tutorialSteps = CONSTANTS.tutorial.steps.filter(s => isMobile ? !s.isDesktopOnly : !s.isMobileOnly);
  const currentStep = tutorialSteps[step];

  const handleNext = () => {
    if (step < tutorialSteps.length - 1) {
      setStep(s => s + 1);
      setActionCompleted(false);
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(s => s - 1);
      setActionCompleted(false);
    }
  };

  const highlightRef = currentStep.highlight ? refs[currentStep.highlight] : null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-start justify-start p-4 sm:p-8 pointer-events-none">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      
      {highlightRef && <HighlightBox targetRef={highlightRef} />}

      <button
        onClick={onExit}
        className="absolute top-4 right-4 px-4 py-2 text-sm bg-black/50 hover:bg-black/70 border border-white/20 rounded transition-colors pointer-events-auto"
      >
        Exit Tutorial
      </button>

      <motion.div
        key={step}
        className="relative w-full max-w-md bg-[#0c0e11]/90 border border-[#2f3540] rounded-lg shadow-2xl p-6 text-left pointer-events-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <h3 className="text-xl font-serif text-yellow-400 mb-2 text-center">{currentStep.title}</h3>
        <p className="text-sm text-gray-300 mb-6">
          {isMobile && currentStep.mobileText ? currentStep.mobileText : currentStep.text}
        </p>

        <div className="flex items-center justify-between">
          <button
            onClick={handlePrev}
            disabled={step === 0}
            className="px-4 py-2 text-sm rounded bg-[#1f2328] hover:bg-[#2a2f36] border border-[#2f3540] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <div className="text-xs text-gray-500">
            {step + 1} / {tutorialSteps.length}
          </div>

          {currentStep.action === 'next' && (
            <button
              onClick={handleNext}
              className="px-4 py-2 text-sm rounded bg-yellow-600 hover:bg-yellow-500 text-black border border-yellow-500"
            >
              Next
            </button>
          )}
          {currentStep.action === 'combine' && (
            <button
              onClick={handleNext}
              disabled={!actionCompleted}
              className="px-4 py-2 text-sm rounded bg-yellow-600 hover:bg-yellow-500 text-black border border-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          )}
          {currentStep.action === 'finish' && (
            <button
              onClick={onExit}
              className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-500 text-white border border-green-500"
            >
              Finish Tutorial
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default App;
